/* Vision Zero — shared ASCII engine (VZA).
   Loaded by the password page and the store layout. */
/* ═══════════════ ASCII ENGINE ═══════════════
   One character-grid renderer drives the intro, the boot screen, the
   hand-off wipe, the live logo and the operator panels. Glyphs are pre-drawn
   into a colour atlas, so thousands of cells per frame stay smooth.
   Strictly black and white; the only colour is a faint red / blue split on
   edges and tears — chromatic aberration, light catching a prism. */
const VZA = (function(){
  const RAMP   = ' .,:;-~=+*xX%#@';
  const NOISE  = '01<>/\\|_-+*#%&@$?!=:;';
  const GLITCH = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789#@%&*<>/\\~';
  const CHARS  = Array.from(new Set((RAMP + NOISE + '*-|=:.~').split(''))).join('');
  const CI = {}; CHARS.split('').forEach((c, i) => { CI[c] = i; });
  // palette buckets: greys → whites (3-7 shimmer between near-whites), then the two aberration channels
  const PAL = [[92,92,92],[138,138,138],[190,190,190],[212,212,212],[230,230,230],[255,255,255],[244,244,244],[222,222,222],[255,70,100],[70,160,255]];
  const GREY = 0, MID = 1, LIGHT = 2, ICE = 4, WHITE = 5, RED = 8, BLUE = 9;
  const MONO = "'Inconsolata', monospace";
  const fontsReady = (document.fonts && document.fonts.load)
    ? Promise.race([Promise.all([document.fonts.load("700 40px 'Inconsolata'")]), new Promise(r => setTimeout(r, 1200))])
    : Promise.resolve();
  const mouse = {x: -9999, y: -9999};
  addEventListener('pointermove', e => { mouse.x = e.clientX; mouse.y = e.clientY; }, {passive: true});
  // a finger lifting off the screen leaves no pointer behind (effects ease back on their own)
  const lift = e => { if (e.pointerType !== 'mouse'){ mouse.x = -9999; mouse.y = -9999; } };
  addEventListener('pointerup', lift, {passive: true});
  addEventListener('pointercancel', lift, {passive: true});

  const clamp = (v, a, b) => v < a ? a : v > b ? b : v;
  const ease  = p => 1 - Math.pow(1 - clamp(p, 0, 1), 3);
  const pick  = s => s[Math.random() * s.length | 0];
  function hash(x, y){ const s = Math.sin(x * 127.1 + y * 311.7) * 43758.5453; return s - Math.floor(s); }
  function vnoise(x, y){
    const xi = Math.floor(x), yi = Math.floor(y), xf = x - xi, yf = y - yi;
    const u = xf * xf * (3 - 2 * xf), v = yf * yf * (3 - 2 * yf);
    const a = hash(xi, yi), b = hash(xi + 1, yi), c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
    return a + (b - a) * u + (c - a) * v + (a - b - c + d) * u * v;
  }
  function opalIdx(x, y, t){
    const s = (Math.sin(x * 0.09 + t * 0.0004) + Math.sin(y * 0.13 - t * 0.0003) + Math.sin((x + y) * 0.05 + t * 0.0002)) / 3;
    return 3 + Math.min(4, (s * 0.5 + 0.5) * 5 | 0);
  }
  // dark-on-white drawing → 0..1 coverage per cell (throws if the source is cross-origin)
  function sample(off, cols, rows, cw, ch){
    const W = off.width, H = off.height, px = off.getContext('2d').getImageData(0, 0, W, H).data;
    const m = new Float32Array(cols * rows);
    for (let y = 0; y < rows; y++) for (let x = 0; x < cols; x++){
      let s = 0;
      for (let j = 0; j < 2; j++) for (let i = 0; i < 2; i++){
        const sx = Math.min(W - 1, (x + 0.25 + i * 0.5) * cw | 0), sy = Math.min(H - 1, (y + 0.25 + j * 0.5) * ch | 0);
        const k = (sy * W + sx) * 4;
        s += 1 - (px[k] * 0.299 + px[k + 1] * 0.587 + px[k + 2] * 0.114) / 255;
      }
      m[y * cols + x] = s / 4;
    }
    return m;
  }
  function blank(w, h){
    const c = document.createElement('canvas');
    c.width = Math.max(1, Math.round(w)); c.height = Math.max(1, Math.round(h));
    const o = c.getContext('2d'); o.fillStyle = '#fff'; o.fillRect(0, 0, c.width, c.height);
    return c;
  }

  /* a canvas divided into cw × ch character cells, drawn from a glyph atlas */
  function Surface(canvas, cw, ch, maxDpr){
    this.c = canvas; this.ctx = canvas.getContext('2d');
    this.cw = cw; this.ch = ch; this.gate = 1; this.maxDpr = maxDpr || 1.5;
    this.resize();
  }
  Surface.prototype.resize = function(){
    const r = this.c.getBoundingClientRect(), dpr = this.dpr = Math.min(devicePixelRatio || 1, this.maxDpr);   // glyphs stay crisp at 1.5×; 2× nearly doubles the pixels to fill
    this.w = r.width; this.h = r.height;
    this.c.width = Math.max(1, Math.round(r.width * dpr)); this.c.height = Math.max(1, Math.round(r.height * dpr));
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.cols = Math.ceil(r.width / this.cw); this.rows = Math.ceil(r.height / this.ch);
    buildAtlas(this);
  };
  function buildAtlas(s){
    const gw = s.gw = Math.ceil(s.cw * s.dpr), gh = s.gh = Math.ceil(s.ch * s.dpr);
    const a = s.atlas || (s.atlas = document.createElement('canvas'));
    a.width = gw * CHARS.length; a.height = gh * PAL.length;
    const ac = a.getContext('2d');
    ac.font = Math.max(1, Math.round(s.ch * 0.86 * s.dpr)) + 'px ' + MONO; ac.textBaseline = 'top'; ac.textAlign = 'center';
    PAL.forEach((p, pi) => {
      ac.fillStyle = 'rgb(' + p + ')';
      for (let i = 0; i < CHARS.length; i++) ac.fillText(CHARS[i], i * gw + gw / 2, pi * gh + gh * 0.08);
    });
  }
  /* a second, finer character grid drawn onto an existing surface's canvas */
  function View(parent, cw, ch){
    this.parent = parent; this.c = parent.c; this.ctx = parent.ctx;
    this.cw = cw; this.ch = ch; this.gate = 1;
    this.refresh();
  }
  View.prototype = Object.create(Surface.prototype);
  View.prototype.refresh = function(){
    const p = this.parent;
    this.dpr = p.dpr; this.w = p.w; this.h = p.h;
    this.cols = Math.ceil(this.w / this.cw); this.rows = Math.ceil(this.h / this.ch);
    buildAtlas(this);
  };
  Surface.prototype.clear = function(){ this.ctx.globalAlpha = 1; this.ctx.clearRect(0, 0, this.w, this.h); };
  Surface.prototype.at = function(m, x, y){
    return (x < 0 || y < 0 || x >= this.cols || y >= this.rows) ? 0 : m[y * this.cols + x];
  };
  Surface.prototype.glyph = function(ch, x, y, ci, a){
    const gi = CI[ch];
    if (gi === undefined || a < 0.03 || (this.gate < 1 && Math.random() > this.gate)) return;
    this.ctx.globalAlpha = a > 1 ? 1 : a;
    this.ctx.drawImage(this.atlas, gi * this.gw, ci * this.gh, this.gw, this.gh, x * this.cw, y * this.ch, this.cw, this.ch);
  };
  // lit cell: chrome grey at low density → opal at high density
  Surface.prototype.lit = function(x, y, v, t, scramble){
    const ch = scramble ? pick(NOISE) : RAMP[clamp(1 + (v * (RAMP.length - 2)) | 0, 1, RAMP.length - 1)];
    this.glyph(ch, x, y, v < 0.35 ? GREY : v < 0.55 ? MID : v < 0.7 ? LIGHT : opalIdx(x, y, t), 0.3 + v * 0.7);
  };
  // edge cells pick up the prism's chromatic halo: cyan on one side, ember on the other
  Surface.prototype.fringe = function(m, x, y){
    if (this.at(m, x - 1, y) > 0.5) this.glyph(':', x, y, BLUE, 0.4);
    else if (this.at(m, x + 1, y) > 0.5) this.glyph(':', x, y, RED, 0.34);
  };
  // four-point star glint built from characters, with a soft streak underneath
  Surface.prototype.glint = function(gx, gy, life){
    const a = Math.sin(clamp(life, 0, 1) * Math.PI), L = 2 + Math.round(a * 5), ctx = this.ctx;
    const cx = (gx + 0.5) * this.cw, cy = (gy + 0.5) * this.ch, R = (L + 3) * this.cw, V = (L * 0.6 + 2) * this.ch;
    ctx.globalAlpha = 1;
    let g = ctx.createLinearGradient(cx - R, 0, cx + R, 0);
    g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.5, 'rgba(255,255,255,' + (a * 0.55).toFixed(2) + ')'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(cx - R, cy - 0.5, R * 2, 1);
    g = ctx.createLinearGradient(0, cy - V, 0, cy + V);
    g.addColorStop(0, 'rgba(255,255,255,0)'); g.addColorStop(0.5, 'rgba(255,255,255,' + (a * 0.55).toFixed(2) + ')'); g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g; ctx.fillRect(cx - 0.5, cy - V, 1, V * 2);
    this.glyph('*', gx, gy, WHITE, a);
    for (let i = 1; i <= L; i++){
      const f = a * (1 - i / (L + 1));
      this.glyph('-', gx - i, gy, ICE, f); this.glyph('-', gx + i, gy, ICE, f);
      if (i <= L * 0.6){ this.glyph('|', gx, gy - i, ICE, f); this.glyph('|', gx, gy + i, ICE, f); }
    }
  };
  // slice glitch: copy random horizontal bands of the canvas onto itself, displaced
  Surface.prototype.slice = function(n, amp){
    const d = this.dpr, ctx = this.ctx;
    ctx.globalAlpha = 1;
    for (let i = 0; i < n; i++){
      const y = Math.random() * this.h, h = 3 + Math.random() * 28, dx = (Math.random() - 0.5) * amp;
      ctx.drawImage(this.c, 0, y * d, this.w * d, h * d, dx, y, this.w, h);
    }
  };

  /* ── 3D point cloud of a jagged crystal on a molten base (the Chapter 001 form, built in code) ── */
  function makePrism(n){
    const pts = new Float32Array(n * 4);
    for (let i = 0; i < n; i++){
      const k = i + 0.5, phi = Math.acos(1 - 2 * k / n), th = Math.PI * (1 + Math.sqrt(5)) * k;
      const x = Math.cos(th) * Math.sin(phi), y = Math.cos(phi), z = Math.sin(th) * Math.sin(phi);
      const base = y < -0.4;
      const f = base ? 0.95 + 0.25 * vnoise(x * 7 + 3, z * 7 + y * 5)
                     : 0.76 + 0.32 * Math.round(vnoise(x * 2.2 + 7, z * 2.2 + y * 1.7) * 4) / 4;
      pts[i * 4] = x * f * 0.8; pts[i * 4 + 1] = y * f * 1.25; pts[i * 4 + 2] = z * f * 0.8; pts[i * 4 + 3] = base ? 1 : 0;
    }
    return pts;
  }
  // rotate + project; nearest point per cell wins, shaded by facing and depth
  function drawPrism(s, pts, cx, cy, hCells, t, burst, show, alpha, o){
    const n = pts.length / 4, len = s.cols * s.rows;
    if (!s.zb || s.zb.length !== len){ s.zb = new Float32Array(len).fill(-99); s.lum = new Float32Array(len); s.kind = new Uint8Array(len); s.hit = new Int32Array(len); }
    const zb = s.zb, lum = s.lum, kind = s.kind, hit = s.hit;
    // o.ry: externally driven spin; o.disp: dispersion — the form splits into 5 sliding spectral layers
    const ry = o && o.ry !== undefined ? o.ry : t * 0.0005, rx = 0.32 + Math.sin(t * 0.0003) * 0.14, disp = o && o.disp || 0;
    const cr = Math.cos(ry), sr = Math.sin(ry), cX = Math.cos(rx), sX = Math.sin(rx);
    const k = hCells / 2.5 * 2.6, ax = s.ch / s.cw, lim = n * clamp(show, 0, 1) | 0;
    let hits = 0;
    for (let i = 0; i < lim; i++){
      let x = pts[i * 4], y = pts[i * 4 + 1], z = pts[i * 4 + 2];
      if (burst){ const b = 1 + burst * (0.6 + hash(i, 3.1) * 3); x *= b; y *= b; z *= b; }
      let x1 = x * cr - z * sr, z1 = x * sr + z * cr;
      const y1 = y * cX - z1 * sX; z1 = y * sX + z1 * cX;
      const band = clamp((y + 1.3) / 2.6 * 5 | 0, 0, 4);
      if (disp) x1 += disp * (band - 2) * (1 + Math.sin(t * 0.002 + band) * 0.3);
      const p = 1 / (2.6 - z1), gx = Math.round(cx + x1 * k * p * ax), gy = Math.round(cy - y1 * k * p);
      if (gx < 0 || gy < 0 || gx >= s.cols || gy >= s.rows) continue;
      const j = gy * s.cols + gx;
      if (z1 <= zb[j]) continue;
      if (zb[j] === -99) hit[hits++] = j;
      zb[j] = z1;
      const r = Math.sqrt(x1 * x1 + y1 * y1 + z1 * z1) || 1, facing = (x1 * -0.45 + y1 * 0.55 + z1 * 0.7) / r;
      lum[j] = clamp(0.18 + 0.82 * Math.max(0, facing) * (0.55 + 0.45 * (z1 + 1) / 2), 0, 1);
      // outermost spectral layers pick up a faint red / blue split once the form disperses
      kind[j] = disp > 0.02 && (band === 0 || band === 4) && hash(i, 5.3) < disp * 2.2 ? (band === 0 ? 3 : 4)
              : pts[i * 4 + 3] ? 1 : (hash(i, 7.7) < 0.035 ? 2 : 0);
    }
    const fade = clamp(1 - (burst || 0) * 0.85, 0, 1) * (alpha === undefined ? 1 : alpha);
    for (let h = 0; h < hits; h++){
      const j = hit[h], x = j % s.cols, y = j / s.cols | 0, v = lum[j];
      const ch = RAMP[clamp(1 + v * (RAMP.length - 2) | 0, 1, RAMP.length - 1)];
      const kd = kind[j];
      const ci = kd === 3 ? RED : kd === 4 ? BLUE : kd === 2 && v > 0.35 ? WHITE : kd === 1 ? (v > 0.75 ? WHITE : v > 0.45 ? LIGHT : MID) : (v > 0.5 ? opalIdx(x, y, t) : v > 0.3 ? LIGHT : MID);
      s.glyph(ch, x, y, ci, (0.35 + v * 0.65) * fade * (kd > 2 ? 0.6 : 1));
      zb[j] = -99;
    }
    return hits;
  }
  // TouchDesigner-style viewer frame: corner ticks + operator labels
  function hud(s, cx, cy, wC, hC, labels, a){
    if (a <= 0.02) return;
    const ctx = s.ctx, x0 = (cx - wC / 2) * s.cw, y0 = (cy - hC / 2) * s.ch, w = wC * s.cw, h = hC * s.ch, L = 12;
    ctx.globalAlpha = a; ctx.strokeStyle = 'rgba(255,255,255,0.75)'; ctx.lineWidth = 1;
    ctx.beginPath();
    [[x0, y0, 1, 1], [x0 + w, y0, -1, 1], [x0, y0 + h, 1, -1], [x0 + w, y0 + h, -1, -1]].forEach(([x, y, dx, dy]) => {
      ctx.moveTo(x + dx * L, y + 0.5); ctx.lineTo(x, y + 0.5); ctx.lineTo(x, y + dy * L);
    });
    ctx.moveTo(x0 + w / 2 - 5, y0 + h / 2); ctx.lineTo(x0 + w / 2 + 5, y0 + h / 2);
    ctx.moveTo(x0 + w / 2, y0 + h / 2 - 5); ctx.lineTo(x0 + w / 2, y0 + h / 2 + 5);
    ctx.stroke();
    ctx.font = '10px ' + MONO;
    ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.textBaseline = 'bottom'; ctx.textAlign = 'left'; ctx.fillText(labels[0], x0, y0 - 6);
    ctx.fillStyle = 'rgba(232,232,232,0.7)'; ctx.textAlign = 'right'; ctx.fillText(labels[1], x0 + w, y0 - 6);
    ctx.textBaseline = 'top'; ctx.textAlign = 'left'; ctx.fillText(labels[2], x0, y0 + h + 6);
    ctx.textAlign = 'right'; ctx.fillText(labels[3], x0 + w, y0 + h + 6);
    ctx.textAlign = 'left'; ctx.globalAlpha = 1;
  }

  /* ── full-screen field: power-on → morphing "hello." → power-off → boot scene → break → wipe ── */
  const field = {
    init(canvas){
      const cw = innerWidth < 600 ? 5 : 6;
      this.cv = canvas; this.s = new Surface(canvas, cw, Math.round(cw * 1.6));
      fontsReady.then(() => this.s.resize());
      this.phase = 'power'; this.t0 = performance.now();
      this.tear = null; this.nextTear = this.t0 + 2200; this.glints = [];
      addEventListener('resize', () => {
        if (this.phase === 'done') return;
        this.s.resize();
        if (this.hv) this.hv.refresh();
        if (this.text) this.buildHello(this.text);
      });
      this.frame = this.frame.bind(this);
      requestAnimationFrame(this.frame);
    },
    // the original symbol → letter morph: every slot cycles random symbols,
    // letters lock in left to right (60ms ticks, 5 per letter)
    hello(text, onTyped){
      fontsReady.then(() => {
        // the word lives on its own finer grid so it can stay small and still read as ASCII
        this.hv = new View(this.s, 1.8, 2.9);
        this.text = text; this.buildHello(text);
        this.slots = text.split('').map(() => pick(GLITCH));
        this.morph = {revealed: 0, count: 0, next: performance.now(), done: false, onTyped};
      });
    },
    buildHello(text){
      const hv = this.hv, size = clamp(hv.w * 0.02, 22, 30);
      const o = document.createElement('canvas').getContext('2d');
      o.font = '800 ' + size + 'px ' + MONO;
      this.size = size; this.cache = {};
      this.slotCols = Math.max(1, Math.round(o.measureText('M').width / hv.cw));
      this.textRows = Math.max(1, Math.round(size / hv.ch));
      this.x0 = Math.round((hv.cols - this.slotCols * text.length) / 2);
      this.y0 = Math.round(hv.rows / 2 - this.textRows / 2);
      this.box = {top: this.y0 * hv.ch, bottom: (this.y0 + this.textRows) * hv.ch};
    },
    charMask(c){
      if (this.cache[c]) return this.cache[c];
      const s = this.hv, W = this.slotCols * s.cw, H = this.textRows * s.ch, off = blank(W, H), o = off.getContext('2d');
      o.font = '800 ' + this.size + 'px ' + MONO; o.textAlign = 'center'; o.textBaseline = 'middle'; o.fillStyle = '#000';
      o.fillText(c, W / 2, H / 2 + this.size * 0.04);
      o.lineWidth = this.size * 0.04; o.strokeStyle = '#000'; o.strokeText(c, W / 2, H / 2 + this.size * 0.04);
      return (this.cache[c] = sample(off, this.slotCols, this.textRows, s.cw, s.ch));
    },
    tick(){
      const M = this.morph, T = this.text;
      if (M.revealed > T.length){ M.done = true; M.onTyped && M.onTyped(this.box); return; }
      M.count++;
      for (let i = 0; i < T.length; i++){
        if (i < M.revealed) this.slots[i] = T[i];
        else this.slots[i] = (i === M.revealed && M.count % 3 === 0) ? T[i] : pick(GLITCH);
      }
      if (M.count >= 5){ M.revealed++; M.count = 0; }
    },
    powerOff(cb){ this.phase = 'off'; this.tOff = performance.now(); this.onOff = cb; },
    finishOff(now){
      this.phase = 'boot'; this.bootT0 = now; this.slots = null; this.text = null;
      this.prism = this.prism || makePrism(innerWidth < 600 ? 1800 : 4200);
      const cb = this.onOff; this.onOff = null; cb && cb();
    },
    breakTo(cb){
      if (this.phase === 'done'){ cb(); return; }
      this.phase = 'break'; this.tBreak = performance.now(); this.onBreak = cb;
    },
    end(){ this.phase = 'done'; this.cv.classList.add('off'); },
    frame(now){
      if (this.phase === 'done') return;
      if (!(now - this.lastDraw < 30)){ this.lastDraw = now; this.draw(now); }   // ~30fps
      requestAnimationFrame(this.frame);
    },
    draw(now){
      const s = this.s, ctx = s.ctx, cx = s.cols / 2, cy = s.rows / 2, t = now - this.t0;
      s.clear();

      const M = this.morph;
      if (M && !M.done && this.slots){
        if (now - M.next > 300) M.next = now;
        while (!M.done && now >= M.next){ this.tick(); M.next += 60; }
      }
      if (this.phase === 'break' && now - this.tBreak >= 280){
        this.phase = 'wipe'; this.tWipe = now;
        const cb = this.onBreak; this.onBreak = null; cb && cb();
      }

      // signal tear: a few rows slip sideways for a beat
      if (now > this.nextTear){
        const h = 1 + (Math.random() * 3 | 0);
        this.tear = {y: (Math.random() * (s.rows - h)) | 0, h, dx: (Math.random() < 0.5 ? -1 : 1) * (2 + (Math.random() * 6 | 0)), until: now + 90};
        if (this.slots && Math.random() < 0.6) this.tear.y = ((this.y0 + Math.random() * this.textRows) * this.hv.ch / s.ch) | 0;
        this.nextTear = now + 1400 + Math.random() * 2200;
      }
      const tear = this.tear && now < this.tear.until ? this.tear : null;

      // band = rows lit either side of centre (CRT power on / off)
      let band = cy + 1, lineW = cx, boost = 1, dot = 0, haze = 1;
      if (this.phase === 'power'){
        if (t > 650) this.phase = 'live';
        else if (t < 220){ band = 0; lineW = ease(t / 220) * cx; }
        else band = ease((t - 220) / 430) * cy;
      } else if (this.phase === 'off'){
        const p = (now - this.tOff) / 420;
        boost = 1.35;
        if (p >= 1){ this.finishOff(now); return; }
        if (p < 0.55) band = (1 - ease(p / 0.55)) * cy;
        else if (p < 0.85){ band = 0; lineW = (1 - (p - 0.55) / 0.3) * cx; }
        else { band = 0; lineW = 0; dot = 1 - (p - 0.85) / 0.15; }
      } else if (this.phase !== 'live') haze = 0.35;

      // wipe: a scrambling band sweeps down, uncovering the page; black below it
      let front = -99;
      if (this.phase === 'wipe'){
        const p = (now - this.tWipe) / 700;
        if (p >= 1){ this.end(); s.clear(); return; }
        front = ease(p) * (s.rows + 16) - 8;
        ctx.globalAlpha = 1; ctx.fillStyle = '#050505';
        ctx.fillRect(0, Math.max(0, (front + 5) * s.ch), s.w, s.h);
      }

      for (let y = 0; y < s.rows; y++){
        if (front > -99){
          if (y < front - 14) continue;
          if (y < front - 5){   // residue left behind the band
            const r = 1 - (front - 5 - y) / 9;
            for (let x = 0; x < s.cols; x++) if (Math.random() < 0.05 * r) s.glyph(pick(NOISE), x, y, Math.random() < 0.3 ? LIGHT : MID, 0.5 * r);
            continue;
          }
          if (y <= front + 5){
            const k = 1 - Math.abs(y - front) / 6, dx = ((hash(y, now / 40 | 0) - 0.5) * 14) | 0;
            for (let x = 0; x < s.cols; x++){
              if (Math.random() > 0.3 + k * 0.6) continue;
              const r = Math.random();
              s.glyph(pick(NOISE), x + dx, y, r < 0.035 ? RED : r < 0.07 ? BLUE : (k > 0.6 ? opalIdx(x, y, now) : LIGHT), 0.35 + k * 0.65);
            }
            continue;
          }
        }
        const dy = Math.abs(y + 0.5 - cy);
        if (dy > band + 0.5){
          if (band < 1 && dy < 1) for (let x = 0; x < s.cols; x++) if (Math.abs(x + 0.5 - cx) < lineW) s.glyph('-', x, y, WHITE, 0.95);
          if (dot > 0 && dy < 1) s.glint(cx | 0, y, 1 - dot * 0.5);
          continue;
        }
        const edge = band < cy && Math.abs(dy - band) < 1;
        const shift = tear && y >= tear.y && y < tear.y + tear.h ? tear.dx : 0;
        for (let x = 0; x < s.cols; x++){
          if (edge){ s.glyph('=', x, y, ICE, 0.55); continue; }
          const n = vnoise((x - shift) * 0.07 + now * 0.00012, y * 0.11 - now * 0.00006);
          if (n > 0.7) s.glyph(n > 0.82 ? ':' : '.', x, y, MID, (n - 0.7) * 2.2 * haze);
        }
      }

      // the morphing word, on its finer grid; clipped by the CRT band, torn with the field
      if (this.slots && front === -99){
        const hv = this.hv, limit = (band + 0.5) * s.ch, midY = s.h / 2, span = this.slotCols * this.slots.length;
        for (let y = this.y0; y < this.y0 + this.textRows; y++){
          const py = (y + 0.5) * hv.ch;
          if (Math.abs(py - midY) > limit) continue;
          const fy = py / s.ch | 0;
          const shift = tear && fy >= tear.y && fy < tear.y + tear.h ? Math.round(tear.dx * s.cw / hv.cw) : 0;
          for (let x = this.x0 - 30; x < this.x0 + span + 30; x++){
            const lx = x - shift - this.x0;
            if (lx < 0 || lx >= span) continue;
            const si = lx / this.slotCols | 0;
            let v = this.charMask(this.slots[si])[(y - this.y0) * this.slotCols + lx - si * this.slotCols];
            if (si >= this.morph.revealed) v *= 0.8;
            if (v <= 0.06) continue;
            hv.lit(x, y, Math.min(1, v * boost), now, !!shift);
            if (shift && Math.random() < 0.3) hv.glyph(pick(NOISE), x - Math.sign(shift) * 3, y, shift > 0 ? RED : BLUE, 0.45);
          }
        }
      }

      // boot scene: the Chapter 001 form as a rotating point cloud in a viewer frame
      if (this.phase === 'boot' || this.phase === 'break'){
        const wide = s.w >= 900, bt = now - this.bootT0;
        // phones: the viewer sits in the top third, clear of the boot log that fills the bottom
        const pcx = wide ? s.cols * 0.74 : s.cols * 0.5, pcy = wide ? s.rows * 0.5 : s.rows * 0.25;
        const hC = wide ? s.rows * 0.56 : Math.min(s.rows * 0.19, s.cols * s.cw / s.ch * 0.5);
        const burst = this.phase === 'break' ? ease((now - this.tBreak) / 280) : 0, show = ease(bt / 1100);
        const hits = drawPrism(s, this.prism, pcx, pcy, hC, now, burst, show);
        if (!this.hudAt || now - this.hudAt > 180){
          this.hudAt = now;
          this.hudTxt = ['sop1 · prism_001.geo', 'pts ' + (this.prism.length / 4 * show | 0), 'rot.y ' + ((now * 0.0005 * 57.2958) % 360).toFixed(1) + '°', 'cook ' + (0.5 + Math.random() * 0.6).toFixed(2) + 'ms · ' + hits + ' cells'];
        }
        hud(s, pcx, pcy, hC * 0.82 * s.ch / s.cw, hC * 1.12, this.hudTxt, show * (1 - burst));
        if (show >= 1 && !burst && Math.random() < 0.02) this.glints.push({x: pcx + (Math.random() - 0.5) * hC * 0.9 | 0, y: pcy + (Math.random() - 0.2) * hC * 0.6 | 0, born: now});
        this.glints = this.glints.filter(g => now - g.born < 900);
        this.glints.forEach(g => s.glint(g.x, g.y, (now - g.born) / 900));
        if (this.phase === 'break') s.slice(8, 80);
        else if (Math.random() < 0.025) s.slice(2, 30);
      }

      // cursor: thin bar after the string, sharp on, slow phosphor fade off
      if (this.slots && (this.phase === 'live' || this.phase === 'power')){
        const ph = (now % 1100) / 1100, a = ph < 0.5 ? 1 : Math.exp(-(ph - 0.5) * 9);
        ctx.globalAlpha = a; ctx.fillStyle = 'rgb(240,240,240)';
        ctx.fillRect((this.x0 + this.slotCols * this.slots.length) * this.hv.cw + this.size * 0.06, this.box.top + this.size * 0.12, Math.max(2, this.size * 0.035), (this.box.bottom - this.box.top) * 0.76);
      }
      ctx.globalAlpha = 1;
    }
  };

  /* ── live ASCII logo, extruded into a 3D object ──
     The flat logo becomes a point cloud with depth: a lit front face and
     stacked side walls. Its points fly in out of noise on arrival, it tilts
     toward the cursor, scrambles near it, tears, and catches glints. */
  function logo(img){
    if (!img || img.dataset.ascii) return;
    img.dataset.ascii = '1';
    const wrap = img.parentElement, PAD = 40, src = new Image();
    src.crossOrigin = 'anonymous';
    src.onload = () => fontsReady.then(() => {
      const cv = document.createElement('canvas');
      cv.className = 'logo-ascii'; cv.setAttribute('aria-hidden', 'true');
      wrap.appendChild(cv);
      let s, m, P, from, del, zb, kd, srcCell, hit, cov;
      let start = performance.now(), glints = [], nextGlint = start + 1600, visible = true;
      let tear = null, nextTear = start + 2600, tiltX = 0, tiltY = 0;

      function build(){
        const w = img.getBoundingClientRect().width, h = w * src.naturalHeight / src.naturalWidth;
        const cw = clamp(w / 80, 3.2, 6.5);
        s = new Surface(cv, cw, cw * 1.55);
        const off = blank(s.w, s.h);
        off.getContext('2d').drawImage(src, PAD, PAD, w, h);
        m = sample(off, s.cols, s.rows, s.cw, s.ch);
        // front face for every filled cell; side walls stacked along z behind the rim
        const list = [], depth = w * 0.07, cx = s.w / 2, cy = s.h / 2;
        for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++){
          if (m[y * s.cols + x] < 0.35) continue;
          const px = (x + 0.5) * s.cw - cx, py = (y + 0.5) * s.ch - cy;
          const rim = s.at(m, x - 1, y) < 0.3 || s.at(m, x + 1, y) < 0.3 || s.at(m, x, y - 1) < 0.3 || s.at(m, x, y + 1) < 0.3;
          list.push(px, py, depth / 2, rim ? 2 : 1, x, y);
          if (rim) for (let k = 1; k <= 5; k++) list.push(px, py, depth / 2 - depth * k / 5, 0, x, y);
        }
        P = new Float32Array(list);
        const n = P.length / 6;
        from = new Float32Array(n * 3); del = new Float32Array(n);
        for (let i = 0; i < n; i++){
          from[i * 3] = (hash(i, 1.1) - 0.5) * s.w * 1.6;
          from[i * 3 + 1] = (hash(i, 2.2) - 0.5) * s.h * 1.6;
          from[i * 3 + 2] = (hash(i, 3.3) - 0.5) * 700;
          del[i] = hash(i, 4.4) * 650 + (P[i * 6 + 5] / s.rows) * 400;
        }
        const len = s.cols * s.rows;
        zb = new Float32Array(len).fill(-1e9); kd = new Uint8Array(len); srcCell = new Int32Array(len); hit = new Int32Array(len); cov = new Uint8Array(len);
      }
      try { build(); } catch (e) { cv.remove(); return; }   // cross-origin logo: keep the plain image
      wrap.classList.add('ascii-on');
      if ('IntersectionObserver' in window) new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(cv);
      let rt;
      addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { try { build(); } catch (e) {} }, 150); });

      function draw(now){
        const t = now - start, r = cv.getBoundingClientRect(), C = s.cols, Rw = s.rows;
        const mx = mouse.x - r.left, my = mouse.y - r.top, R = Math.max(70, s.w * 0.16), has = mouse.x > -9999;
        // tilt toward the cursor (eased) over a slow idle sway
        // strong twist: the cursor at the screen's edge turns the mark ~60° on its side, ~40° on its tilt
        const tx = has ? clamp((mouse.y - (r.top + r.height / 2)) / (innerHeight * 0.5), -1, 1) * 0.7 : 0;
        const ty = has ? clamp((mouse.x - (r.left + r.width / 2)) / (innerWidth * 0.5), -1, 1) * 1.05 : 0;
        tiltX += (tx + Math.sin(t * 0.0007) * 0.05 - tiltX) * 0.07;
        tiltY += (ty + Math.sin(t * 0.0005) * 0.14 - tiltY) * 0.07;
        const ca = Math.cos(tiltY), sa = Math.sin(tiltY), cb = Math.cos(tiltX), sb = Math.sin(tiltX), F = 900;
        const cx = s.w / 2, cy = s.h / 2, n = P.length / 6;
        if (now > nextTear){
          tear = {y: Math.random() * Rw | 0, h: 2 + (Math.random() * 6 | 0), dx: (Math.random() < 0.5 ? -1 : 1) * (3 + (Math.random() * 8 | 0)), until: now + 110 + Math.random() * 90};
          nextTear = now + 1300 + Math.random() * 2600;
        }
        const tr = tear && now < tear.until ? tear : null;
        const scanY = t > 1800 ? ((t - 1800) % 5600) / 5600 * (Rw + 24) - 12 : -99;

        // project: nearest point per cell wins
        let hits = 0;
        for (let i = 0; i < n; i++){
          let x = P[i * 6], y = P[i * 6 + 1], z = P[i * 6 + 2];
          const age = t - del[i];
          let flying = false;
          if (age < 700){
            if (age < 0 && hash(i, now / 90 | 0) > 0.06) continue;   // a few early sparks ahead of the swarm
            const k = ease(Math.max(0, age) / 700);
            x = from[i * 3] + (x - from[i * 3]) * k; y = from[i * 3 + 1] + (y - from[i * 3 + 1]) * k; z = from[i * 3 + 2] + (z - from[i * 3 + 2]) * k;
            flying = k < 0.98;
          }
          const x1 = x * ca + z * sa, z1 = -x * sa + z * ca;
          const y1 = y * cb - z1 * sb, z2 = y * sb + z1 * cb;
          const f = F / (F - z2);
          let gx = Math.round((cx + x1 * f) / s.cw - 0.5), gy = Math.round((cy + y1 * f) / s.ch - 0.5);
          if (tr && gy >= tr.y && gy < tr.y + tr.h) gx += tr.dx;
          if (has){
            const px = (gx + 0.5) * s.cw - mx, py = (gy + 0.5) * s.ch - my, d2 = px * px + py * py;
            if (d2 < R * R){ const d = Math.sqrt(d2) || 1, nr = 1 - d / R; gx += Math.round(px / d * nr * 4); gy += Math.round(py / d * nr * 2); }
          }
          if (gx < 0 || gy < 0 || gx >= C || gy >= Rw) continue;
          const j = gy * C + gx;
          if (z2 <= zb[j]) continue;
          if (zb[j] === -1e9) hit[hits++] = j;
          zb[j] = z2; kd[j] = flying ? 3 : P[i * 6 + 3]; srcCell[j] = P[i * 6 + 5] * C + P[i * 6 + 4];
        }

        // shade: the face brightens as it turns toward the light, walls catch it as it turns away
        const faceLight = clamp(0.8 - tiltY * 0.5 + tiltX * 0.35, 0.35, 1), wallLight = clamp(0.3 + Math.abs(tiltY) * 1.1 + Math.abs(tiltX) * 0.6, 0.22, 0.85);
        s.clear();
        for (let h = 0; h < hits; h++){
          const j = hit[h], x = j % C, y = j / C | 0, k = kd[j], sc = srcCell[j], sx = sc % C, sy = sc / C | 0;
          let v, scramble = k === 3;
          if (k === 0) v = wallLight * (0.7 + 0.3 * hash(x, y));
          else if (k === 2) v = Math.max(0.9, faceLight);
          else if (k === 1){
            const light = 1 - (sx / C) * 0.4 - (sy / Rw) * 0.3;
            const tone = clamp(vnoise(sx * 0.12 + t * 0.00025, sy * 0.18 - t * 0.0002) * 0.75 + light * 0.45 - 0.15, 0, 1);
            v = m[sc] * (0.3 + 0.7 * tone) * faceLight;
          } else v = 0.55;
          if (Math.abs(y - scanY) < 1.5){ scramble = true; v = Math.min(1, v + 0.25); }
          if (tr && y >= tr.y && y < tr.y + tr.h){ scramble = true; if (Math.random() < 0.5) s.glyph(pick(NOISE), x - Math.sign(tr.dx) * 2, y, tr.dx > 0 ? RED : BLUE, 0.5); }
          if (has){
            const px = (x + 0.5) * s.cw - mx, py = (y + 0.5) * s.ch - my, d2 = px * px + py * py;
            if (d2 < R * R && Math.random() < (1 - Math.sqrt(d2) / R) * 0.8) scramble = true;
          }
          s.lit(x, y, v, now, scramble);
          cov[j] = 1;
        }
        // chromatic fringe on the silhouette: red to the left, blue to the right
        for (let h = 0; h < hits; h++){
          const j = hit[h], x = j % C;
          if (kd[j] === 3) continue;
          if (x > 0 && !cov[j - 1]) s.glyph(':', x - 1, j / C | 0, RED, 0.32);
          if (x < C - 1 && !cov[j + 1]) s.glyph(':', x + 1, j / C | 0, BLUE, 0.36);
        }
        for (let h = 0; h < hits; h++){ cov[hit[h]] = 0; zb[hit[h]] = -1e9; }

        if (now > nextGlint && glints.length < 3 && hits){
          const j = hit[Math.random() * hits | 0];
          if (kd[j] !== 3) glints.push({x: j % C, y: j / C | 0, born: now});
          nextGlint = now + 600 + Math.random() * 1400;
        }
        glints = glints.filter(g => now - g.born < 1100);
        glints.forEach(g => s.glint(g.x, g.y, (now - g.born) / 1100));
        if (tr) s.slice(2, 24);
        s.ctx.globalAlpha = 1;
      }
      let lastDraw = 0;
      function frame(now){
        // 30fps is plenty for characters, and halves the work
        if (visible && now - lastDraw >= 33){ lastDraw = now; draw(now); }
        requestAnimationFrame(frame);
      }
      requestAnimationFrame(frame);
    });
    src.src = img.currentSrc || img.src;
  }

  /* ── operator rack: TouchDesigner-style live panels either side of the gate ── */
  function rack(){
    if (innerWidth < 1300 || innerHeight < 640 || document.querySelector('.td-rack')) return;
    document.body.classList.add('td-on');
    const OPS = {
      left:  [['sop1', 'prism_001', 'prism'], ['noise1', 'field', 'noise'], ['chop2', 'spectrum', 'wave']],
      right: [['fb1', 'feedback', 'feedback'], ['info1', 'vz_core', 'info']]
    };
    const units = [];
    let energy = 0;
    addEventListener('keydown', () => { energy = Math.min(1, energy + 0.3); });
    Object.keys(OPS).forEach(side => {
      const col = document.createElement('div');
      col.className = 'td-rack td-' + side; col.setAttribute('aria-hidden', 'true');
      OPS[side].forEach(([op, name, kind], i) => {
        const el = document.createElement('div');
        el.className = 'td-op';
        el.innerHTML = '<div class="td-head"><b>' + op + '</b><span>' + name + '</span><i></i></div>' +
          (kind === 'info' ? '<pre class="td-info"></pre>' : '<canvas></canvas>');
        col.appendChild(el);
        units.push({el, kind, delay: 500 + (side === 'left' ? 0 : 260) + i * 240});
      });
      document.body.appendChild(col);
    });
    units.forEach(u => {
      if (u.kind === 'info'){ u.pre = u.el.querySelector('pre'); return; }
      u.s = new Surface(u.el.querySelector('canvas'), 4.5, 7);
      if (u.kind === 'prism') u.pts = makePrism(560);
      if (u.kind === 'feedback'){ u.a = new Float32Array(u.s.cols * u.s.rows); u.b = new Float32Array(u.a.length); }
    });
    wires(units);
    const start = performance.now();
    let last = 0, frames = 0, fps = 60, fpsT = start, infoAt = 0;
    const pad = (n, w) => String(Math.max(0, n | 0)).padStart(w, '0');

    function frame(now){
      requestAnimationFrame(frame);
      frames++;
      if (now - fpsT > 500){ fps = frames * 1000 / (now - fpsT); frames = 0; fpsT = now; }
      if (now - last < 32) return;
      last = now; energy *= 0.94;
      units.forEach(u => {
        const age = now - start - u.delay;
        if (age < 0) return;
        if (!u.on){ u.on = true; u.el.classList.add('on'); if (u.wire) u.wire.forEach(w => w.classList.add('on')); }
        if (u.kind === 'info'){
          if (now - infoAt < 200) return;
          infoAt = now;
          const up = (now - start) / 1000, lines = [
            'fps     ' + fps.toFixed(0),
            'cook    ' + (0.3 + Math.random() * 0.4).toFixed(2) + 'ms',
            'cursor  ' + pad(mouse.x, 4) + ' ' + pad(mouse.y, 4),
            'energy  ' + '|'.repeat(1 + energy * 12 | 0).padEnd(13, '.'),
            'uptime  ' + pad(up / 60, 2) + ':' + pad(up % 60, 2),
            'signal  LOCKED'
          ].join('\n');
          // decode in like the logo: unrevealed characters scramble
          const p = clamp(age / 700, 0, 1), cut = lines.length * p | 0;
          u.pre.textContent = p < 1 ? lines.slice(0, cut) + lines.slice(cut).replace(/[^\n ]/g, () => pick(NOISE)) : lines;
          return;
        }
        const s = u.s;
        s.clear();
        s.gate = 0.15 + 0.85 * ease(age / 800);   // assemble in out of noise
        if (u.kind === 'prism') drawPrism(s, u.pts, s.cols / 2, s.rows / 2 + 0.5, s.rows * 0.92, now, 0, 1);
        else if (u.kind === 'noise'){
          for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++){
            const n = vnoise(x * 0.13 + now * 0.0005, y * 0.22 - now * 0.0003) * 0.65 + vnoise(x * 0.4 - now * 0.001, y * 0.6) * 0.35;
            if (n < 0.42) continue;
            const v = (n - 0.42) / 0.58;
            s.glyph(RAMP[1 + v * 13 | 0], x, y, n > 0.84 ? WHITE : v > 0.5 ? LIGHT : GREY, 0.3 + v * 0.7);
          }
        } else if (u.kind === 'wave'){
          const mid = s.rows / 2;
          for (let x = 0; x < s.cols; x++) s.glyph('-', x, Math.round(mid), GREY, 0.3);
          for (let c = 0; c < 3; c++) for (let x = 0; x < s.cols; x++){
            const amp = (s.rows * 0.16 + energy * s.rows * 0.24) * (c === 1 ? 0.6 : 1);
            const y = mid + Math.sin(x * 0.22 + now * 0.004 * (1 + c * 0.3) + c * 2) * amp + (vnoise(x * 0.5 + c * 10, now * 0.004) - 0.5) * 2 * (1 + energy * 3);
            s.glyph(c === 0 ? '*' : c === 1 ? '~' : '.', x, Math.round(y), c === 0 ? WHITE : c === 1 ? LIGHT : MID, 0.9);
          }
        } else if (u.kind === 'feedback'){
          // classic feedback loop: last frame, zoomed out + rotated a touch, decayed, plus the cursor
          const C = s.cols, Rw = s.rows, A = u.a, B = u.b, ca = Math.cos(0.035), sa = Math.sin(0.035);
          for (let y = 0; y < Rw; y++) for (let x = 0; x < C; x++){
            const dx = (x - C / 2) * 0.94, dy = (y - Rw / 2) * 1.6 * 0.94;
            const sx = Math.round(C / 2 + dx * ca - dy * sa), sy = Math.round(Rw / 2 + (dx * sa + dy * ca) / 1.6);
            B[y * C + x] = (sx >= 0 && sy >= 0 && sx < C && sy < Rw) ? A[sy * C + sx] * 0.9 : 0;
          }
          let px, py;
          if (mouse.x > -9999){ px = mouse.x / innerWidth * C; py = mouse.y / innerHeight * Rw; }
          else { px = C / 2 + Math.sin(now * 0.0013) * C * 0.35; py = Rw / 2 + Math.sin(now * 0.0021) * Rw * 0.35; }
          for (let y = -1; y <= 1; y++) for (let x = -2; x <= 2; x++){
            const gx = Math.round(px) + x, gy = Math.round(py) + y;
            if (gx >= 0 && gy >= 0 && gx < C && gy < Rw) B[gy * C + gx] = 1;
          }
          u.a = B; u.b = A;
          for (let y = 0; y < Rw; y++) for (let x = 0; x < C; x++){
            const v = B[y * C + x];
            if (v > 0.06) s.lit(x, y, v, now, Math.random() < 0.05);
          }
        }
        s.gate = 1;
        if (Math.random() < 0.03) s.slice(1, 16);
        s.ctx.globalAlpha = 1;
      });
    }
    requestAnimationFrame(frame);
  }

  /* ── specimen viewer: the Chapter 001 form, live, reacting to the gate ──
     idle → slow spin; typing → spins up and disperses into spectral layers;
     verify → fast spin + scan; deny → shatters, tears, reassembles;
     grant → bursts into light. Used in the gate and the verification screen. */
  function specimen(canvas, opts){
    opts = opts || {};
    const s = new Surface(canvas, opts.cw || 4, opts.ch || 6.4);
    const pts = makePrism(opts.points || (innerWidth < 600 ? 2200 : 3600));
    let ry = 0, spin = 1, energy = 0, disp = 0, level = 0, mode = 'idle', modeT = 0, burst = 0;
    let last = performance.now(), start = last, visible = true, glints = [], hudAt = 0, hudTxt = [];
    fontsReady.then(() => s.resize());
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => s.resize(), 150); });
    if ('IntersectionObserver' in window) new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(canvas);

    function frame(now){
      requestAnimationFrame(frame);
      if (!visible || !s.w || now - last < 33) return;   // 30fps cap
      const dt = Math.min(66, now - last); last = now;
      energy *= 0.95;
      const targetSpin = mode === 'verify' ? 10 : mode === 'grant' ? 16 : 1 + energy * 5;
      spin += (targetSpin - spin) * 0.08;
      ry += dt * 0.0005 * spin;
      const dispTarget = mode === 'verify' ? 0.35 : level * 0.07 + energy * 0.12;
      disp += (dispTarget - disp) * 0.1;
      if (mode === 'deny'){
        const p = (now - modeT) / 1300;
        burst = p < 0.18 ? ease(p / 0.18) * 0.8 : 0.8 * (1 - ease((p - 0.18) / 0.82));
        if (p >= 1){ mode = 'idle'; burst = 0; }
      } else if (mode === 'grant') burst = ease((now - modeT) / 900) * 1.4;
      else burst *= 0.8;

      s.clear();
      const hC = s.rows * (opts.scale || 0.7), show = ease((now - start) / 1400);
      const hits = drawPrism(s, pts, s.cols / 2, opts.hud === false ? s.rows / 2 + 0.5 : s.rows * 0.43, hC, now, burst, show, 1, {ry, disp});
      if (opts.hud !== false){
        if (now - hudAt > 160){
          hudAt = now;
          const nm = 380 + Math.min(1, level / 4) * 320 + energy * 18 | 0;
          hudTxt = [
            opts.label || (s.w < 420 ? 'obs_001' : 'obs_001 · the_unseen_prism'),
            mode === 'verify' ? 'verifying' + '...'.slice(0, (now / 250 | 0) % 4) : mode === 'deny' ? 'KEY REJECTED' : mode === 'grant' ? 'ACCESS GRANTED' : 'pts ' + (pts.length / 4 * show | 0),
            'λ ' + nm + 'nm · disp ' + disp.toFixed(2),
            'rot ' + ((ry * 57.2958) % 360).toFixed(1) + '° · ' + hits + ' cells'
          ];
        }
        hud(s, s.cols / 2, s.rows / 2, s.cols - 6, s.rows - 5.5, hudTxt, show);
      }
      // a scan line sweeps the viewer; fast while verifying
      const period = mode === 'verify' ? 600 : 4200, sy = ((now - start) % period) / period * s.rows | 0;
      for (let x = 2; x < s.cols - 2; x += 2) s.glyph('-', x, sy, LIGHT, mode === 'verify' ? 0.5 : 0.12);
      if (Math.random() < 0.03 + energy * 0.05 && glints.length < 3 && burst < 0.1)
        glints.push({x: s.cols / 2 + (Math.random() - 0.5) * hC * 0.8 * s.ch / s.cw | 0, y: s.rows / 2 + (Math.random() - 0.5) * hC * 0.7 | 0, born: now});
      glints = glints.filter(g => now - g.born < 900);
      glints.forEach(g => s.glint(g.x, g.y, (now - g.born) / 900));
      if (mode === 'deny' && burst > 0.1) s.slice(5, 60);
      else if (Math.random() < 0.015 + energy * 0.06) s.slice(1 + (energy * 3 | 0), 20 + energy * 40);
      s.ctx.globalAlpha = 1;
    }
    requestAnimationFrame(frame);
    return {
      pulse(v){ energy = Math.min(1, energy + (v || 0.4)); },
      level(l){ level = l; },
      verify(){ mode = 'verify'; modeT = performance.now(); },
      deny(){ mode = 'deny'; modeT = performance.now(); },
      grant(){ mode = 'grant'; modeT = performance.now(); },
      idle(){ mode = 'idle'; }
    };
  }

  /* ── node wires: every operator panel is patched into the gate ──
     Left operators feed the gate's input side; the gate feeds the right
     operators. Bezier patch cords with data flowing along them, each drawn
     in as its panel cooks. */
  let wireSvg = null;
  function wires(units){
    const gate = document.getElementById('gate');
    if (!gate) return;
    const NS = 'http://www.w3.org/2000/svg';
    const svg = wireSvg = document.createElementNS(NS, 'svg');
    svg.setAttribute('class', 'td-wires'); svg.setAttribute('aria-hidden', 'true');
    document.body.appendChild(svg);
    const sides = {left: [], right: []};
    units.forEach(u => {
      const side = u.el.parentElement.classList.contains('td-left') ? 'left' : 'right';
      const make = (tag, cls) => { const e = document.createElementNS(NS, tag); e.setAttribute('class', cls); svg.appendChild(e); return e; };
      u.wire = [make('path', 'td-wire'), make('path', 'td-flow' + (side === 'right' ? ' rev' : '')), make('rect', 'td-jack'), make('rect', 'td-jack')];
      sides[side].push(u);
    });
    function jack(el, x, y){ el.setAttribute('x', x - 2.5); el.setAttribute('y', y - 2.5); el.setAttribute('width', 5); el.setAttribute('height', 5); }
    function layout(){
      const g = gate.getBoundingClientRect();
      svg.setAttribute('viewBox', '0 0 ' + innerWidth + ' ' + innerHeight);
      ['left', 'right'].forEach(side => sides[side].forEach((u, i, arr) => {
        const r = u.el.getBoundingClientRect(), L = side === 'left';
        const sx = L ? r.right : r.left, sy = r.top + r.height / 2;
        const ex = L ? g.left : g.right, ey = g.top + 70 + Math.max(0, g.height - 140) * (i + 0.5) / arr.length;
        const mx = (sx + ex) / 2, d = 'M' + sx + ',' + sy + ' C' + mx + ',' + sy + ' ' + mx + ',' + ey + ' ' + ex + ',' + ey;
        const [base, flow, a, b] = u.wire;
        base.setAttribute('d', d); flow.setAttribute('d', d);
        base.style.setProperty('--len', base.getTotalLength());
        jack(a, sx, sy); jack(b, ex, ey);
      }));
    }
    layout();
    addEventListener('resize', layout);
    document.body.addEventListener('scroll', layout, {passive: true});
    addEventListener('scroll', layout, {passive: true});
    setInterval(layout, 700);   // the gate grows when the request drawer opens
  }

  /* ── light path: the page drawn as the prism diagram ──
     A faint ASCII ray enters from the left, disappears behind the gate (the
     prism) and leaves as a fan of spectral rays. Photons travel the paths,
     the cursor bends rays it comes near, and typing the key widens the
     spectrum. Wide screens only; sits behind everything at low intensity. */
  let beamApi = null;
  function beam(target){
    if (beamApi || innerWidth < 900 || !target) return;
    const cv = document.createElement('canvas');
    cv.id = 'vzBeam'; cv.setAttribute('aria-hidden', 'true');
    document.body.insertBefore(cv, document.body.firstChild);
    const s = new Surface(cv, 7, 11), RAYS = 5;
    fontsReady.then(() => s.resize());
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => s.resize(), 150); });
    let level = 0, spread = 0, energy = 0, photons = [], nextPhoton = 0, last = 0;
    const start = performance.now();

    // a ray is a straight segment, pushed away from the cursor where it passes close
    function at(r, p, mx, my){
      let x = r.x0 + (r.x1 - r.x0) * p, y = r.y0 + (r.y1 - r.y0) * p;
      const dx = x - mx, dy = y - my, d = Math.sqrt(dx * dx + dy * dy);
      if (d < 140 && d > 0.1) y += dy / d * (1 - d / 140) * 26;
      return [x, y];
    }
    let pcx = 0, pcy = 0, prx = 0, pry = 0;
    function hidden(rects, x, y){
      if (prx){ const ex = (x - pcx) / prx, ey = (y - pcy) / pry; if (ex * ex + ey * ey < 1) return true; }   // inside the prism itself
      for (let i = 0; i < rects.length; i++){ const r = rects[i]; if (x > r.left - 8 && x < r.right + 8 && y > r.top - 8 && y < r.bottom + 8) return true; }
      return false;
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (now - last < 33 || !s.w) return;
      last = now;
      const g = target.getBoundingClientRect(), gate = target.closest('.gate');
      if (!g.width || !gate) return;
      energy *= 0.93;
      spread += ((0.07 + level * 0.035 + energy * 0.05) - spread) * 0.08;
      const framed = getComputedStyle(gate).boxShadow !== 'none';   // an older boxed gate hides the beam at its edge
      const rects = (framed ? [gate] : [...document.querySelectorAll('.gate-body, .gv-note, .gv-scale')]).concat([...document.querySelectorAll('.td-op, .logo-ascii, .manifesto, .top-bar, .bottom-bar')]).map(e => e.getBoundingClientRect());
      const cx = g.left + g.width / 2, cy = g.top + g.height / 2;
      // the prism's silhouette: rays end where they meet it
      pcx = cx; pcy = cy - g.height * 0.02;
      prx = framed ? 0 : Math.min(g.width * 0.2, g.height * 0.28); pry = framed ? 0 : g.height * 0.36;
      const mx = mouse.x, my = mouse.y, t = now - start;
      const rays = [{x0: -20, y0: cy - s.w * 0.1, x1: cx, y1: cy, c: WHITE, a: 0.3}];
      for (let i = 0; i < RAYS; i++){
        const ang = (i - (RAYS - 1) / 2) * spread + 0.05;
        rays.push({x0: cx, y0: cy, x1: s.w + 20, y1: cy + Math.tan(ang) * (s.w + 20 - cx), c: i === 0 ? RED : i === RAYS - 1 ? BLUE : i === 2 ? WHITE : LIGHT, a: (i === 0 || i === RAYS - 1 ? 0.26 : 0.22) + level * 0.03 + energy * 0.1});
      }
      s.clear();
      rays.forEach(r => {
        const len = Math.hypot(r.x1 - r.x0, r.y1 - r.y0), steps = len / s.cw | 0;
        for (let k = 0; k <= steps; k++){
          const [x, y] = at(r, k / steps, mx, my);
          if (hidden(rects, x, y)) continue;
          const shimmer = 0.75 + 0.25 * Math.sin(k * 0.4 - t * 0.004);
          s.glyph((k + (t / 90 | 0)) % 11 === 0 ? '=' : '-', x / s.cw | 0, y / s.ch | 0, r.c, r.a * shimmer);
        }
      });
      // photons: stream in along the incoming ray, split across the fan on exit
      if (now > nextPhoton){ photons.push({r: 0, p: 0, v: 0.006 + Math.random() * 0.004}); nextPhoton = now + 380 + Math.random() * 500; }
      const born = [];
      photons = photons.filter(ph => {
        ph.p += ph.v * (1 + energy * 2);
        if (ph.p >= 1){
          if (ph.r === 0) born.push(1 + (Math.random() * RAYS | 0));
          return false;
        }
        const r = rays[ph.r], [x, y] = at(r, ph.p, mx, my);
        if (!hidden(rects, x, y)){
          const gx = x / s.cw | 0, gy = y / s.ch | 0;
          s.glyph('*', gx, gy, ph.r === 0 ? WHITE : r.c, 0.85);
          for (let k = 1; k <= 4; k++) s.glyph('-', gx - k, gy, r.c, 0.5 * (1 - k / 5));   // trail behind
        }
        return true;
      });
      born.forEach(ri => photons.push({r: ri, p: 0, v: 0.005 + Math.random() * 0.004}));
      s.ctx.globalAlpha = 1;
    }
    requestAnimationFrame(frame);
    beamApi = {
      pulse(){ energy = Math.min(1, energy + 0.4); photons.push({r: 0, p: 0.55 + Math.random() * 0.2, v: 0.012}); },
      level(l){ level = l; }
    };
  }

  /* ── crosshair cursor with a live coordinate readout (fine pointers only) ──
     Snaps into corner brackets over anything you can use and names it. */
  function cursor(){
    if (!matchMedia('(pointer: fine)').matches || document.querySelector('.vz-cursor')) return;
    const el = document.createElement('div');
    el.className = 'vz-cursor'; el.setAttribute('aria-hidden', 'true');
    el.innerHTML = '<i class="vc-h"></i><i class="vc-v"></i><b class="vc-box"></b><span class="vc-label"></span>';
    document.body.appendChild(el);
    document.documentElement.classList.add('vz-cursor-on');
    const label = el.querySelector('.vc-label'), pad = n => String(Math.max(0, n | 0)).padStart(4, '0');
    let x = innerWidth / 2, y = innerHeight / 2, tx = x, ty = y, lastLabel = '';
    addEventListener('pointermove', e => { tx = e.clientX; ty = e.clientY; el.classList.remove('gone'); }, {passive: true});
    addEventListener('pointerdown', () => el.classList.add('down'));
    addEventListener('pointerup', () => el.classList.remove('down'));
    document.addEventListener('mouseleave', () => el.classList.add('gone'));
    let awake = false, hx = -1, hy = -1, hov = null;
    addEventListener('pointermove', () => { if (!awake){ awake = true; requestAnimationFrame(loop); } }, {passive: true});
    addEventListener('scroll', () => { hx = -1; if (!awake){ awake = true; requestAnimationFrame(loop); } }, {passive: true, capture: true});
    function loop(){
      x += (tx - x) * 0.35; y += (ty - y) * 0.35;
      el.style.transform = 'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0)';
      // what's under the pointer only changes when it moves (or the page scrolls under it)
      if (tx !== hx || ty !== hy){ hx = tx; hy = ty; hov = document.elementFromPoint(tx, ty); }
      const t = hov && hov.closest('a, button, input, .gate-prompt, .gate-viewer, .intro');
      let text = 'x' + pad(tx) + ' y' + pad(ty);
      if (t){
        if (t.matches('.intro')) text = 'CLICK // BEGIN';
        else if (t.matches('.gate-viewer')) text = 'OBS_001 // ' + text;
        else if (t.matches('input, .gate-prompt')) text = t.matches('#reqEmail') ? 'ENTER EMAIL' : 'ENTER KEY';
        else text = (t.getAttribute('aria-label') || t.textContent || '').trim().replace(/\s+/g, ' ').slice(0, 26).toUpperCase() || 'OPEN';
      }
      // anything can name itself to the cursor (the lens reads "LENS // 1.6×")
      const tag = hov && hov.closest('[data-vz-cursor]');
      if (tag) text = tag.dataset.vzCursor;
      el.classList.toggle('hot', !!t && !tag && !t.matches('.intro, .gate-viewer'));
      if (text !== lastLabel){ label.textContent = text; lastLabel = text; }
      // settled: stop until the pointer moves again
      if (Math.abs(tx - x) < 0.3 && Math.abs(ty - y) < 0.3){ awake = false; return; }
      requestAnimationFrame(loop);
    }
    awake = true; requestAnimationFrame(loop);
  }

  /* ── materialize: a product photo arrives as ASCII made from its own light ──
     The image is sampled into a glyph grid that covers the photo, then the
     cells dissolve away (scrambling as they go) to reveal the real photo.
     Hovering sends an ASCII scan band down it. If the image can't be read
     (no CORS), the grid is drawn from noise instead, so it still arrives coded.
     Returns {reveal, scan}. The caller decides when to reveal. */
  function materialize(img){
    const box = img.parentElement;
    if (!box || img.dataset.vzMat) return null;
    img.dataset.vzMat = '1';
    if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
    const cv = document.createElement('canvas');
    cv.className = 'vz-mat'; cv.setAttribute('aria-hidden', 'true');
    box.appendChild(cv);
    let s = null, lum = null, del = null, src = null, mode = 'cover', t0 = 0, running = false, scanY = -1;

    function sampleImage(){
      lum = new Float32Array(s.cols * s.rows);
      try {
        if (!src || !src.naturalWidth) throw 0;
        const off = document.createElement('canvas'), o = off.getContext('2d');
        off.width = s.cols; off.height = s.rows;
        // crop like object-fit: cover, in cell space (cells are taller than wide)
        const want = s.w / s.h, have = src.naturalWidth / src.naturalHeight;
        let sw = src.naturalWidth, sh = src.naturalHeight, sx = 0, sy = 0;
        if (have > want){ sw = sh * want; sx = (src.naturalWidth - sw) / 2; } else { sh = sw / want; sy = (src.naturalHeight - sh) / 2; }
        o.drawImage(src, sx, sy, sw, sh, 0, 0, s.cols, s.rows);
        const px = o.getImageData(0, 0, s.cols, s.rows).data;
        for (let i = 0; i < lum.length; i++) lum[i] = (px[i * 4] * 0.299 + px[i * 4 + 1] * 0.587 + px[i * 4 + 2] * 0.114) / 255;
        // contrast from the photo's typical range (5th–95th percentile), so a
        // small white label can't flatten a black garment on a dark backdrop
        const sorted = Float32Array.from(lum).sort();
        const lo = sorted[sorted.length * 0.05 | 0], span = Math.max(0.06, sorted[sorted.length * 0.95 | 0] - lo);
        const base = lum.map(v => clamp((v - lo) / span, 0, 1));
        // plus edges, so the garment's outline glows like a traced form
        const C = s.cols, R = s.rows, at = (x, y) => base[clamp(y, 0, R - 1) * C + clamp(x, 0, C - 1)];
        for (let y = 0; y < R; y++) for (let x = 0; x < C; x++){
          const edge = Math.abs(at(x + 1, y) - at(x - 1, y)) + Math.abs(at(x, y + 1) - at(x, y - 1));
          lum[y * C + x] = clamp(0.7 * at(x, y) + 1.6 * edge, 0, 1);
        }
      } catch (e){
        for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) lum[y * s.cols + x] = vnoise(x * 0.15, y * 0.12) * 0.8;
      }
    }
    function build(){
      if (!img.offsetWidth || !img.offsetHeight) return false;
      // sit exactly on the photo, whatever else its container holds
      Object.assign(cv.style, {left: img.offsetLeft + 'px', top: img.offsetTop + 'px', width: img.offsetWidth + 'px', height: img.offsetHeight + 'px'});
      // about 80 cells across whatever the photo's size: big cards no longer mean 10,000+ cells
      const cw = clamp(img.offsetWidth / 100, 4, 8);
      s = new Surface(cv, cw, cw * 1.6, 1);
      sampleImage();
      del = new Float32Array(s.cols * s.rows);
      for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++)
        del[y * s.cols + x] = 150 + hash(x * 1.3, y * 2.1) * 550 + (y / s.rows) * 300;
      return true;
    }
    function cell(x, y, a, scramble, now){
      const v = Math.max(0.12, lum[y * s.cols + x]);
      s.ctx.globalAlpha = a; s.ctx.fillStyle = '#050505';
      s.ctx.fillRect(x * s.cw, y * s.ch, s.cw + 0.5, s.ch + 0.5);
      s.lit(x, y, v, now, scramble);
      s.ctx.globalAlpha = 1;
    }
    let lastDraw = 0;
    function frame(now){
      if (!s){ running = false; return; }
      if (now - lastDraw < 33 && mode !== 'cover'){ requestAnimationFrame(frame); return; }   // 30fps
      lastDraw = now;
      s.clear();
      const t = now - t0;
      let busy = false;
      if (mode === 'cover' || mode === 'reveal'){
        for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++){
          const age = mode === 'cover' ? -1 : t - del[y * s.cols + x];
          if (age < 0){ cell(x, y, 1, Math.random() < 0.06, now); busy = true; }
          else if (age < 260){ cell(x, y, 1 - age / 260, Math.random() < 0.5, now); busy = true; }
        }
        if (mode === 'cover') busy = false;   // the waiting cover is drawn once, not animated off-screen
      }
      if (scanY > -99){
        const p = (now - scanY) / 620, row = p * (s.rows + 12) - 6;
        if (p >= 1) scanY = -99;
        else {
          busy = true;
          for (let y = Math.max(0, row - 4 | 0); y <= Math.min(s.rows - 1, row + 4 | 0); y++){
            const k = 1 - Math.abs(y - row) / 5;
            for (let x = 0; x < s.cols; x++){
              if (Math.random() > 0.35 + k * 0.6) continue;
              cell(x, y, 0.85 * k, Math.random() < 0.35, now);
              if (Math.random() < 0.04) s.glyph(':', x + (Math.random() < 0.5 ? -1 : 1), y, Math.random() < 0.5 ? RED : BLUE, 0.6 * k);
            }
          }
        }
      }
      if (busy) requestAnimationFrame(frame);
      else { if (mode !== 'cover') s.clear(); running = false; }
    }
    function kick(){ if (!running){ running = true; requestAnimationFrame(frame); } }

    // until the source loads, cover with noise so nothing pops in un-coded
    if (build()){ mode = 'cover'; kick(); }
    src = new Image();
    src.crossOrigin = 'anonymous';
    src.onload = () => { if (s) sampleImage(); else build(); if (mode === 'cover') kick(); };
    src.src = img.currentSrc || img.src;
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (mode !== 'cover') build(); }, 200); });
    return {
      reveal(){ if (!s && !build()) return; mode = 'reveal'; t0 = performance.now(); kick(); },
      scan(){ if (!s || mode === 'cover') return; scanY = performance.now(); kick(); }
    };
  }

  /* ── luminance grid of an image (CORS), auto-contrasted; null if unreadable ── */
  function lumaOf(src, W){
    try {
      const H = Math.max(1, Math.round(W * src.naturalHeight / src.naturalWidth));
      const off = document.createElement('canvas'), o = off.getContext('2d');
      off.width = W; off.height = H;
      o.drawImage(src, 0, 0, W, H);
      const px = o.getImageData(0, 0, W, H).data, lum = new Float32Array(W * H);
      for (let i = 0; i < lum.length; i++) lum[i] = (px[i * 4] * 0.299 + px[i * 4 + 1] * 0.587 + px[i * 4 + 2] * 0.114) / 255;
      const sorted = Float32Array.from(lum).sort();
      const lo = sorted[sorted.length * 0.05 | 0], span = Math.max(0.06, sorted[sorted.length * 0.95 | 0] - lo);
      for (let i = 0; i < lum.length; i++) lum[i] = clamp((lum[i] - lo) / span, 0, 1);
      return {lum, W, H};
    } catch (e){ return null; }
  }

  /* ── prism lens: a liquid-glass loupe over product photos ──
     A disc of glass follows the cursor on a soft spring and stretches along
     its direction of travel. Its rim bends the photo (magnification rising
     toward the edge) and splits light red / blue; its core shows the unseen
     layer: the same spot rebuilt as ASCII. A specular crescent, rim light and
     a cast shadow sell the glass. Fine pointers only. */
  // the glass itself, per pixel on the GPU: smooth refraction that bends harder
  // toward the rim, with red / green / blue dispersing by slightly different
  // amounts there (a real prism edge), plus rim light, specular and cast shadow
  const LENS_FS = [
    'precision mediump float;',
    'uniform sampler2D u_img; uniform vec2 u_res; uniform vec2 u_c; uniform float u_r; uniform float u_mag;',
    'uniform vec4 u_fit; uniform vec3 u_dir; varying vec2 v_uv;',
    'vec3 tap(vec2 p){ vec2 uv = (p - u_fit.xy) / u_fit.zw; if (uv.x < 0.0 || uv.y < 0.0 || uv.x > 1.0 || uv.y > 1.0) return vec3(0.02); return texture2D(u_img, uv).rgb; }',
    'void main(){',
    '  vec2 p = v_uv * u_res; vec2 d = p - u_c;',
    '  vec2 q = vec2(d.x * u_dir.x + d.y * u_dir.y, -d.x * u_dir.y + d.y * u_dir.x);',
    '  q = vec2(q.x / u_dir.z, q.y * u_dir.z);',
    '  float rr = length(q) / u_r;',
    '  if (rr > 1.0) {',
    '    float sd = length(p - u_c - vec2(5.0, 12.0)) / u_r;',
    '    float a = 0.5 * (1.0 - smoothstep(0.75, 1.45, sd));',
    '    gl_FragColor = vec4(0.0, 0.0, 0.0, a); return; }',
    '  float edge = smoothstep(0.5, 1.0, rr);',
    '  float g = 1.0 + 1.5 * pow(rr, 3.0);',
    '  vec2 dir = d / u_mag;',
    '  vec3 col = vec3(tap(u_c + dir * g * (1.0 - 0.045 * edge)).r, tap(u_c + dir * g).g, tap(u_c + dir * g * (1.0 + 0.045 * edge)).b);',
    '  vec2 n = rr > 0.001 ? q / (rr * u_r) : vec2(0.0);',
    '  float spec = pow(max(0.0, dot(n, normalize(vec2(-0.55, -0.83)))), 6.0) * smoothstep(0.55, 0.95, rr);',
    '  float glow = exp(-pow((rr - 0.5) * 2.2, 2.0)) * pow(max(0.0, dot(n, normalize(vec2(-0.4, -0.9)))), 2.0) * 0.18;',
    '  col += spec * 0.55 + glow + smoothstep(0.9, 1.0, rr) * 0.22;',
    '  col *= 1.0 - smoothstep(0.2, 1.0, max(0.0, dot(n, vec2(0.0, 1.0)))) * 0.25 * edge;',
    '  float a = 1.0 - smoothstep(0.985, 1.0, rr);',
    '  gl_FragColor = vec4(col * a, a);',
    '}'
  ].join('\n');
  function lensGL(canvas){
    const gl = canvas.getContext('webgl', {premultipliedAlpha: true, alpha: true, antialias: false});
    if (!gl) return null;
    const sh = (type, code) => { const o = gl.createShader(type); gl.shaderSource(o, code); gl.compileShader(o); return gl.getShaderParameter(o, gl.COMPILE_STATUS) ? o : null; };
    const vs = sh(gl.VERTEX_SHADER, 'attribute vec2 a; varying vec2 v_uv; void main(){ v_uv = vec2(a.x * 0.5 + 0.5, 0.5 - a.y * 0.5); gl_Position = vec4(a, 0.0, 1.0); }');
    const fs = sh(gl.FRAGMENT_SHADER, LENS_FS);
    if (!vs || !fs) return null;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs); gl.attachShader(prog, fs); gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return null;
    gl.useProgram(prog);
    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, 'a');
    gl.enableVertexAttribArray(loc); gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const U = {}; ['u_res', 'u_c', 'u_r', 'u_mag', 'u_fit', 'u_dir'].forEach(n => { U[n] = gl.getUniformLocation(prog, n); });
    const tex = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tex);
    [gl.TEXTURE_WRAP_S, gl.TEXTURE_WRAP_T].forEach(p => gl.texParameteri(gl.TEXTURE_2D, p, gl.CLAMP_TO_EDGE));
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    let ready = false;
    return {
      load(image){
        try { gl.bindTexture(gl.TEXTURE_2D, tex); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image); ready = true; }
        catch (e){ ready = false; }
        return ready;
      },
      get ready(){ return ready; },
      draw(w, h, dpr, c, r, mag, fit, dir){
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)){ canvas.width = Math.round(w * dpr); canvas.height = Math.round(h * dpr); }
        gl.viewport(0, 0, canvas.width, canvas.height);
        gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform2f(U.u_res, w, h); gl.uniform2f(U.u_c, c[0], c[1]); gl.uniform1f(U.u_r, r); gl.uniform1f(U.u_mag, mag);
        gl.uniform4f(U.u_fit, fit.ox, fit.oy, fit.iw * fit.k, fit.ih * fit.k); gl.uniform3f(U.u_dir, dir[0], dir[1], dir[2]);
        gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      },
      clear(){ gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT); }
    };
  }

  function lens(img){
    const box = img.parentElement;
    if (!box || box.dataset.vzLens || !matchMedia('(pointer: fine)').matches) return null;
    box.dataset.vzLens = '1';
    const glCv = document.createElement('canvas');
    glCv.className = 'vz-lens vz-lens--glass'; glCv.setAttribute('aria-hidden', 'true');
    box.appendChild(glCv);
    const cv = document.createElement('canvas');
    cv.className = 'vz-lens'; cv.setAttribute('aria-hidden', 'true');
    box.appendChild(cv);
    const G = lensGL(glCv);
    let s = null, L = null;
    const src = new Image();
    src.crossOrigin = 'anonymous';
    src.onload = () => { L = lumaOf(src, 260); if (G) G.load(src); };
    src.src = img.currentSrc || img.src;
    const st = {x: 0, y: 0, tx: 0, ty: 0, vx: 0, vy: 0, scale: 0, target: 0, running: false, mag: 1.6};
    box.dataset.vzCursor = 'LENS // ' + st.mag.toFixed(1) + '×';

    function ensure(){
      const w = box.clientWidth, h = box.clientHeight;
      if (s && Math.abs(s.w - w) < 1 && Math.abs(s.h - h) < 1) return;
      Object.assign(cv.style, {left: '0px', top: '0px', width: w + 'px', height: h + 'px'});
      Object.assign(glCv.style, {left: '0px', top: '0px', width: w + 'px', height: h + 'px'});
      s = new Surface(cv, 6, 9);
    }
    // object-fit: cover mapping between the box and the image's own pixels
    function fit(){
      const iw = img.naturalWidth || 1, ih = img.naturalHeight || 1, k = Math.max(s.w / iw, s.h / ih);
      return {k, iw, ih, ox: (s.w - iw * k) / 2, oy: (s.h - ih * k) / 2};
    }
    function lumAt(x, y, F){
      if (!L) return 0.4;
      const u = (x - F.ox) / F.k / F.iw, v = (y - F.oy) / F.k / F.ih;
      if (u < 0 || v < 0 || u >= 1 || v >= 1) return 0;
      return L.lum[(v * L.H | 0) * L.W + (u * L.W | 0)];
    }
    function frame(now){
      ensure();
      const ctx = s.ctx, dpr = s.dpr, R = clamp(Math.min(s.w, s.h) * 0.17, 64, 120);
      // spring follow: the glass lags and settles, it doesn't snap
      st.vx = st.vx * 0.6 + (st.tx - st.x) * 0.2; st.vy = st.vy * 0.6 + (st.ty - st.y) * 0.2;
      st.x += st.vx; st.y += st.vy;
      st.scale += (st.target - st.scale) * (st.target ? 0.16 : 0.24);
      s.clear();
      if (st.target === 0 && st.scale < 0.01){ st.running = false; if (G) G.clear(); return; }
      const r = R * st.scale, F = fit(), core = r * 0.66;
      const speed = Math.hypot(st.vx, st.vy), ang = Math.atan2(st.vy, st.vx), stretch = 1 + Math.min(0.2, speed * 0.011);
      const body = () => { ctx.translate(st.x, st.y); ctx.rotate(ang); ctx.scale(stretch, 1 / stretch); ctx.rotate(-ang); };
      const glOK = G && G.ready;

      if (glOK){
        G.draw(s.w, s.h, dpr, [st.x, st.y], r, st.mag, F, [Math.cos(ang), Math.sin(ang), stretch]);
        ctx.save();
        ctx.globalAlpha = 1;
        const dim = ctx.createRadialGradient(st.x, st.y, core * 0.55, st.x, st.y, core);
        dim.addColorStop(0, 'rgba(5,5,5,0.66)'); dim.addColorStop(1, 'rgba(5,5,5,0)');
        ctx.fillStyle = dim; ctx.beginPath(); ctx.arc(st.x, st.y, core, 0, Math.PI * 2); ctx.fill();
        ctx.restore();
      } else {
      // fallback glass (no WebGL): stacked refraction rings on the 2D canvas
      // cast shadow
      ctx.save();
      ctx.globalAlpha = 1;
      const sh = ctx.createRadialGradient(st.x + 5, st.y + 12, r * 0.55, st.x + 5, st.y + 12, r * 1.4);
      sh.addColorStop(0, 'rgba(0,0,0,0.5)'); sh.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = sh; ctx.fillRect(st.x - r * 1.7, st.y - r * 1.7, r * 3.4, r * 3.4);

      // refraction: rings from the rim inward, magnification rising toward the edge
      body();
      ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.clip();
      const N = 12;
      for (let i = N; i >= 1; i--){
        const m = st.mag * (1 + 0.6 * Math.pow(i / N, 4)), half = r / m;
        ctx.save();
        ctx.beginPath(); ctx.arc(0, 0, r * i / N, 0, Math.PI * 2); ctx.clip();
        ctx.drawImage(img, (st.x - half - F.ox) / F.k, (st.y - half - F.oy) / F.k, 2 * half / F.k, 2 * half / F.k, -r, -r, 2 * r, 2 * r);
        ctx.restore();
      }
      // the core dims so the unseen layer can come through
      const dim = ctx.createRadialGradient(0, 0, core * 0.55, 0, 0, core);
      dim.addColorStop(0, 'rgba(5,5,5,0.66)'); dim.addColorStop(1, 'rgba(5,5,5,0)');
      ctx.fillStyle = dim; ctx.fillRect(-r, -r, 2 * r, 2 * r);
      ctx.restore();
      }

      // the unseen layer: the magnified spot rebuilt as ASCII, fading toward the rim
      ctx.save();
      ctx.beginPath(); ctx.arc(st.x, st.y, core, 0, Math.PI * 2); ctx.clip();
      const x0 = Math.max(0, (st.x - core) / s.cw | 0), x1 = Math.min(s.cols - 1, (st.x + core) / s.cw | 0);
      const y0 = Math.max(0, (st.y - core) / s.ch | 0), y1 = Math.min(s.rows - 1, (st.y + core) / s.ch | 0);
      for (let gy = y0; gy <= y1; gy++) for (let gx = x0; gx <= x1; gx++){
        const px = (gx + 0.5) * s.cw, py = (gy + 0.5) * s.ch, d = Math.hypot(px - st.x, py - st.y);
        if (d > core) continue;
        const v = lumAt(st.x + (px - st.x) / st.mag, st.y + (py - st.y) / st.mag, F);
        if (v < 0.08) continue;
        const ch = Math.random() < 0.04 ? pick(NOISE) : RAMP[clamp(1 + v * (RAMP.length - 2) | 0, 1, RAMP.length - 1)];
        s.glyph(ch, gx, gy, v < 0.35 ? MID : v < 0.6 ? LIGHT : opalIdx(gx, gy, now), (0.35 + 0.65 * v) * (1 - Math.pow(d / core, 3)));
      }
      ctx.restore();

      // glass: chromatic rim, rim light, specular crescent, thickness shading
      // (the GPU path draws its own; this is for the fallback)
      if (!glOK){
      ctx.save();
      body();
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'lighter';
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255,60,90,0.5)'; ctx.beginPath(); ctx.arc(-1.6, 0, r - 1.5, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = 'rgba(60,150,255,0.5)'; ctx.beginPath(); ctx.arc(1.6, 0, r - 1.5, 0, Math.PI * 2); ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
      ctx.beginPath(); ctx.arc(0, 0, r, 0, Math.PI * 2); ctx.clip();
      const edge = ctx.createRadialGradient(0, 0, r * 0.82, 0, 0, r);
      edge.addColorStop(0, 'rgba(255,255,255,0)'); edge.addColorStop(1, 'rgba(255,255,255,0.16)');
      ctx.fillStyle = edge; ctx.fillRect(-r, -r, 2 * r, 2 * r);
      const spec = ctx.createRadialGradient(-r * 0.38, -r * 0.48, 0, -r * 0.38, -r * 0.48, r * 0.72);
      spec.addColorStop(0, 'rgba(255,255,255,0.42)'); spec.addColorStop(0.35, 'rgba(255,255,255,0.1)'); spec.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = spec; ctx.fillRect(-r, -r, 2 * r, 2 * r);
      const thick = ctx.createLinearGradient(0, -r, 0, r);
      thick.addColorStop(0.55, 'rgba(0,0,0,0)'); thick.addColorStop(1, 'rgba(0,0,0,0.28)');
      ctx.fillStyle = thick; ctx.fillRect(-r, -r, 2 * r, 2 * r);
      ctx.lineWidth = 1; ctx.strokeStyle = 'rgba(255,255,255,0.5)';
      ctx.beginPath(); ctx.arc(0, 0, r - 0.5, 0, Math.PI * 2); ctx.stroke();
      ctx.restore();
      }

      requestAnimationFrame(frame);
    }
    function kick(){ if (!st.running){ st.running = true; requestAnimationFrame(frame); } }
    const at = e => { const b = box.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
    box.addEventListener('pointerenter', e => {
      if (e.pointerType !== 'mouse') return;
      [st.tx, st.ty] = at(e);
      if (st.scale < 0.05){ st.x = st.tx; st.y = st.ty; st.vx = st.vy = 0; }
      st.target = 1; kick();
    });
    box.addEventListener('pointermove', e => {
      if (e.pointerType !== 'mouse') return;
      [st.tx, st.ty] = at(e);
      st.target = box.dataset.vzScan ? 0 : 1;   // the lens steps aside while a relief scan is showing
      kick();
    });
    box.addEventListener('pointerleave', () => { st.target = 0; kick(); });
    return {
      setMag(m){ st.mag = clamp(m, 1.2, 3); box.dataset.vzCursor = 'LENS // ' + st.mag.toFixed(1) + '×'; }
    };
  }

  /* ── wordmark: a giant ASCII sign-off ──
     The word is set huge in the house font, sampled into a character grid and
     assembled left to right the first time it scrolls into view. After that
     its light drifts, it ripples away from the cursor, tears now and then and
     catches glints. */
  function wordmark(canvas, text){
    let s = null, m = null, del = null, start = 0, visible = false, last = 0, tear = null, nextTear = 0, glints = [], nextGlint = 0;
    function build(){
      const w = canvas.parentElement.clientWidth;
      if (!w) return false;
      const o = document.createElement('canvas').getContext('2d');
      o.font = '700 100px ' + MONO;
      const size = 100 * (w * 0.96) / o.measureText(text).width;
      canvas.style.height = Math.round(size * 0.8) + 'px';
      const cw = clamp(w / 220, 3, 6);
      s = new Surface(canvas, cw, cw * 1.55);
      const off = blank(s.w, s.h), oc = off.getContext('2d');
      oc.font = '700 ' + size + 'px ' + MONO; oc.textAlign = 'center'; oc.textBaseline = 'middle'; oc.fillStyle = '#000';
      oc.fillText(text, s.w / 2, s.h / 2 + size * 0.03);
      m = sample(off, s.cols, s.rows, s.cw, s.ch);
      del = new Float32Array(s.cols * s.rows);
      for (let y = 0; y < s.rows; y++) for (let x = 0; x < s.cols; x++) del[y * s.cols + x] = (x / s.cols) * 900 + hash(x, y) * 450;
      return true;
    }
    function draw(now){
      const t = now - start, r = canvas.getBoundingClientRect(), mx = mouse.x - r.left, my = mouse.y - r.top, R = Math.max(90, s.h * 0.9);
      if (now > nextTear){
        tear = {y: Math.random() * s.rows | 0, h: 2 + (Math.random() * 5 | 0), dx: (Math.random() < 0.5 ? -1 : 1) * (3 + (Math.random() * 9 | 0)), until: now + 120};
        nextTear = now + 2600 + Math.random() * 3600;
      }
      const tr = tear && now < tear.until ? tear : null;
      s.clear();
      for (let y = 0; y < s.rows; y++){
        const shift = tr && y >= tr.y && y < tr.y + tr.h ? tr.dx : 0;
        for (let x = 0; x < s.cols; x++){
          const i = y * s.cols + x;
          let sx = x - shift, sy = y, near = 0;
          const px = (x + 0.5) * s.cw - mx, py = (y + 0.5) * s.ch - my, d = Math.hypot(px, py);
          if (d < R){
            near = 1 - d / R;
            const wave = Math.sin(d * 0.07 - t * 0.012) * near * 3;
            sx -= Math.round(px / (d || 1) * wave); sy -= Math.round(py / (d || 1) * wave * 0.6);
          }
          let v = s.at(m, sx, sy);
          const age = t - del[i];
          if (age < 0){
            if (v > 0.3 && Math.random() < 0.03) s.glyph(pick(NOISE), x, y, MID, 0.3);
            continue;
          }
          if (v > 0.06){
            const rim = s.at(m, sx - 1, sy) < 0.3 || s.at(m, sx + 1, sy) < 0.3 || s.at(m, sx, sy - 1) < 0.3 || s.at(m, sx, sy + 1) < 0.3;
            v = rim ? Math.max(v, 0.92) : v * (0.5 + 0.5 * vnoise(x * 0.05 + t * 0.00018, y * 0.14 - t * 0.0001));
            let scramble = !!shift || (near > 0.3 && Math.random() < near * 0.7);
            if (age < 300){ v *= age / 300; scramble = scramble || Math.random() > age / 300; }
            s.lit(x, y, v, now, scramble);
            if (shift && Math.random() < 0.4) s.glyph(pick(NOISE), x - Math.sign(shift) * 2, y, shift > 0 ? RED : BLUE, 0.5);
          } else if (!near) s.fringe(m, x, y);
        }
      }
      if (t > 1400 && now > nextGlint && glints.length < 2){
        for (let k = 0; k < 30; k++){ const i = Math.random() * m.length | 0; if (m[i] > 0.8){ glints.push({x: i % s.cols, y: i / s.cols | 0, born: now}); break; } }
        nextGlint = now + 900 + Math.random() * 1800;
      }
      glints = glints.filter(g => now - g.born < 1100);
      glints.forEach(g => s.glint(g.x, g.y, (now - g.born) / 1100));
      if (tr) s.slice(2, 24);
      s.ctx.globalAlpha = 1;
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (!visible || !s || (now - start > 1500 && now - last < 32)) return;
      last = now; draw(now);
    }
    fontsReady.then(() => {
      if (!build()) return;
      if ('IntersectionObserver' in window) new IntersectionObserver(es => {
        visible = es[0].isIntersecting;
        if (visible && !start){ start = performance.now(); nextTear = start + 2600; nextGlint = start + 1600; }
      }, {rootMargin: '0px 0px -10% 0px'}).observe(canvas);
      else { visible = true; start = performance.now(); }
      requestAnimationFrame(frame);
    });
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(build, 200); });
  }

  /* ── relief: a photo as a 3D ASCII surface ──
     The photo's light becomes height: bright areas rise toward you, dark ones
     sink. Switched on, it extrudes out of the flat image over ~0.7s and then
     tilts with the cursor; switched off, it flattens back into the photo.
     Returns {toggle, off, active}. */
  function relief(img){
    const box = img.parentElement;
    if (!box) return null;
    const cv = document.createElement('canvas');
    cv.className = 'vz-relief'; cv.setAttribute('aria-hidden', 'true');
    box.appendChild(cv);
    let s = null, L = null, active = false, running = false, t0 = 0, tiltX = 0, tiltY = 0, rise = 0;
    let zb = null, val = null, hit = null;
    const src = new Image();
    src.crossOrigin = 'anonymous';
    src.onload = () => { L = lumaOf(src, 220); };
    src.src = img.currentSrc || img.src;
    function build(){
      const w = box.clientWidth, h = box.clientHeight;
      Object.assign(cv.style, {left: '0px', top: '0px', width: w + 'px', height: h + 'px'});
      s = new Surface(cv, 5, 8);
      const n = s.cols * s.rows;
      zb = new Float32Array(n).fill(-1e9); val = new Float32Array(n); hit = new Int32Array(n);
    }
    function lumAt(x, y){
      if (!L) return 0.4;
      const iw = img.naturalWidth || 1, ih = img.naturalHeight || 1, k = Math.max(s.w / iw, s.h / ih);
      const u = (x - (s.w - iw * k) / 2) / k / iw, v = (y - (s.h - ih * k) / 2) / k / ih;
      if (u < 0 || v < 0 || u >= 1 || v >= 1) return 0;
      return L.lum[(v * L.H | 0) * L.W + (u * L.W | 0)];
    }
    function frame(now){
      if (!s) build();
      const target = active ? 1 : 0;
      rise += (target - rise) * 0.09;
      if (!active && rise < 0.01){ s.clear(); running = false; cv.style.opacity = 0; return; }
      cv.style.opacity = 1;
      const r = box.getBoundingClientRect(), has = mouse.x > -9999;
      const tx = has ? clamp((mouse.y - (r.top + r.height / 2)) / r.height, -0.6, 0.6) * 0.7 : 0;
      const ty = has ? clamp((mouse.x - (r.left + r.width / 2)) / r.width, -0.6, 0.6) * 0.9 : 0;
      tiltX += (tx + Math.sin(now * 0.0007) * 0.05 - tiltX) * 0.08;
      tiltY += (ty + Math.sin(now * 0.0005) * 0.1 - tiltY) * 0.08;
      const ca = Math.cos(tiltY * rise), sa = Math.sin(tiltY * rise), cb = Math.cos(tiltX * rise), sb = Math.sin(tiltX * rise);
      const C = s.cols, Rw = s.rows, cx = s.w / 2, cy = s.h / 2, depth = s.w * 0.24 * ease(rise), Fz = 1000;
      const ctx = s.ctx;
      s.clear();
      ctx.globalAlpha = Math.min(1, rise * 1.4); ctx.fillStyle = '#050505'; ctx.fillRect(0, 0, s.w, s.h);
      let hits = 0;
      for (let gy = 0; gy < Rw; gy++) for (let gx = 0; gx < C; gx++){
        const px = (gx + 0.5) * s.cw, py = (gy + 0.5) * s.ch, v = lumAt(px, py);
        const x = px - cx, y = py - cy, z = (v - 0.5) * depth;
        const x1 = x * ca + z * sa, z1 = -x * sa + z * ca, y1 = y * cb - z1 * sb, z2 = y * sb + z1 * cb;
        const f = Fz / (Fz - z2);
        const qx = Math.round((cx + x1 * f) / s.cw - 0.5), qy = Math.round((cy + y1 * f) / s.ch - 0.5);
        if (qx < 0 || qy < 0 || qx >= C || qy >= Rw) continue;
        const j = qy * C + qx;
        if (z2 <= zb[j]) continue;
        if (zb[j] === -1e9) hit[hits++] = j;
        zb[j] = z2;
        // light from the upper left across the relief's slopes
        const slope = (lumAt(px + s.cw, py) - lumAt(px - s.cw, py)) * -0.6 + (lumAt(px, py + s.ch) - lumAt(px, py - s.ch)) * -0.8;
        val[j] = clamp(v * (0.75 + slope * 1.6 * rise) + 0.04, 0, 1);
      }
      for (let h = 0; h < hits; h++){
        const j = hit[h], v = val[j];
        if (v > 0.06) s.lit(j % C, j / C | 0, v, now, Math.random() < 0.02 + (1 - rise) * 0.3);
        zb[j] = -1e9;
      }
      const scan = ((now - t0) % 3200) / 3200 * (Rw + 10) - 5;
      for (let x = 0; x < C; x += 2) s.glyph('-', x, scan | 0, LIGHT, 0.18 * rise);
      ctx.globalAlpha = 1;
      requestAnimationFrame(frame);
    }
    function kick(){ if (!running){ running = true; requestAnimationFrame(frame); } }
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (s) build(); }, 200); });
    return {
      toggle(){ active = !active; if (active){ t0 = performance.now(); build(); } box.dataset.vzScan = active ? '1' : ''; kick(); return active; },
      off(){ if (active){ active = false; box.dataset.vzScan = ''; kick(); } },
      get active(){ return active; }
    };
  }

  /* ── story: the chapter's beam ──
     One ray of ASCII light enters from the left, passes through a small
     rotating prism and fans out into a spectrum. progress (0..1, from scroll)
     widens and thickens the fan; photons ride the rays. */
  function story(canvas){
    const s = new Surface(canvas, 7, 11), pts = makePrism(1800), BANDS = 7;
    let p = 0, shown = 0, visible = false, photons = [], nextPh = 0, last = 0;
    fontsReady.then(() => s.resize());
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => s.resize(), 150); });
    if ('IntersectionObserver' in window) new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(canvas);
    else visible = true;
    const bandColor = i => i === 0 ? RED : i === BANDS - 1 ? BLUE : i === (BANDS >> 1) ? WHITE : (i % 2 ? LIGHT : ICE);
    function ray(x0, y0, x1, y1, ci, a, thick, now){
      const steps = Math.max(1, Math.abs(x1 - x0) | 0);
      for (let k = 0; k <= steps; k++){
        const x = x0 + (x1 - x0) * k / steps, y = y0 + (y1 - y0) * k / steps;
        const shimmer = 0.75 + 0.25 * Math.sin(k * 0.35 - now * 0.005);
        for (let w = 0; w < thick; w++){
          const yy = Math.round(y - thick / 2 + w + 0.5);
          s.glyph(w === (thick >> 1) ? '=' : '-', Math.round(x), yy, ci, a * shimmer * (w === (thick >> 1) ? 1 : 0.55));
        }
      }
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (!visible || !s.w || now - last < 30) return;
      last = now;
      shown += (p - shown) * 0.08;
      s.clear();
      const C = s.cols, R = s.rows, cx = C * 0.6, cy = R * 0.5, inY = cy - R * 0.2;
      ray(0, inY, cx - 2, cy, WHITE, 0.55, 1, now);
      const spread = 0.04 + shown * 0.11, thick = 1 + Math.round(shown * 3);
      const ends = [];
      for (let i = 0; i < BANDS; i++){
        const ang = (i - (BANDS - 1) / 2) * spread + 0.02;
        const y1 = cy + Math.tan(ang) * (C - cx);
        ends.push(y1);
        ray(cx + 2, cy, C, y1, bandColor(i), (i === 0 || i === BANDS - 1 ? 0.35 : 0.3) + shown * 0.45, thick, now);
      }
      drawPrism(s, pts, cx, cy, R * 0.34, now, 0, 1, 1, {disp: shown * 0.22});
      // photons: in along the ray, out along a random band
      if (now > nextPh){ photons.push({b: -1, p: 0}); nextPh = now + 300 + Math.random() * 400 - shown * 200; }
      photons = photons.filter(ph => {
        ph.p += 0.012 + shown * 0.01;
        if (ph.p >= 1){ if (ph.b < 0){ ph.b = Math.random() * BANDS | 0; ph.p = 0; return true; } return false; }
        const x = ph.b < 0 ? ph.p * (cx - 2) : cx + 2 + ph.p * (C - cx - 2);
        const y = ph.b < 0 ? inY + (cy - inY) * ph.p : cy + (ends[ph.b] - cy) * ph.p;
        s.glyph('*', Math.round(x), Math.round(y), ph.b < 0 ? WHITE : bandColor(ph.b), 0.95);
        for (let k = 1; k <= 3; k++) s.glyph('-', Math.round(x) - k, Math.round(y), WHITE, 0.45 * (1 - k / 4));
        return true;
      });
      s.ctx.globalAlpha = 1;
    }
    requestAnimationFrame(frame);
    return {progress(v){ p = clamp(v, 0, 1); }};
  }

  /* ── chrome: the logo as liquid black chrome, rendered in characters ──
     The logo's shape is inflated into a soft height field. Every frame that
     surface wobbles like molten metal, its normals reflect a banded studio
     sky (the hard light/dark stripes that read as chrome), and the result is
     set as ASCII: dense glyphs on the highlights, sparse in the shadows, a
     red/blue split where the light is brightest. It tilts toward the cursor,
     ripples under it, drips from its lowest edges and tears now and then. */
  function chrome(canvas, src){
    let s = null, H = null, S = null, rowW = null, colW = null, bx0 = 0, bx1 = 0, by0 = 0, by1 = 0, HW = 0, HH = 0, del = null, start = 0, visible = false, last = 0;
    const sparse = !matchMedia('(hover: hover) and (min-width: 990px)').matches;
    let tiltX = 0, tiltY = 0, rip = 0, rcx = 0, rcy = 0, glints = [], nextGlint = 0, drips = [], nextDrip = 0, tear = null, nextTear = 0, img = null;
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    // the logo's mask, inflated: blur it a few times so the edges round over like a poured blob
    function heightField(){
      // crop to the mark's own edges first (logo files often carry empty padding)
      const bb = document.createElement('canvas'); bb.width = img.naturalWidth; bb.height = img.naturalHeight;
      const bc = bb.getContext('2d'); bc.drawImage(img, 0, 0);
      const bp = bc.getImageData(0, 0, bb.width, bb.height).data;
      let x0 = bb.width, y0 = bb.height, x1 = 0, y1 = 0;
      for (let y = 0; y < bb.height; y += 2) for (let x = 0; x < bb.width; x += 2){
        const q = (y * bb.width + x) * 4;
        const ink = bp[q + 3] > 40 && (bp[q + 3] < 250 || bp[q] + bp[q + 1] + bp[q + 2] < 600);
        if (ink){ if (x < x0) x0 = x; if (x > x1) x1 = x; if (y < y0) y0 = y; if (y > y1) y1 = y; }
      }
      if (x1 <= x0 || y1 <= y0){ x0 = 0; y0 = 0; x1 = bb.width; y1 = bb.height; }
      const iw = x1 - x0 + 2, ih = y1 - y0 + 2;
      // the stage takes the mark's proportions, with room around it to drip and glow
      const bw = canvas.getBoundingClientRect().width;
      canvas.style.height = Math.round(clamp(bw * 0.84 * ih / iw * 1.3, 200, innerHeight * 0.72)) + 'px';
      // desktop gets the fine, full-density metal; phones a chunkier grid
      const full = matchMedia('(hover: hover) and (min-width: 990px)').matches;
      const cw = full ? clamp(bw / 140, 4, 8) : clamp(bw / 115, 5, 9);
      s = new Surface(canvas, cw, cw * 1.55, full ? 1.5 : 1.25);
      HW = s.cols; HH = s.rows;
      const off = document.createElement('canvas'); off.width = HW * 4; off.height = HH * 4;
      const oc = off.getContext('2d');
      // a cell is taller than wide, so the mark is squashed vertically on this grid to keep its proportions
      const sq = s.cw / s.ch;
      const k = Math.min(off.width * 0.84 / iw, off.height * 0.84 / (ih * sq));
      const dw = iw * k, dh = ih * k * sq;
      oc.drawImage(img, x0, y0, iw, ih, (off.width - dw) / 2, (off.height - dh) / 2, dw, dh);
      const px = oc.getImageData(0, 0, off.width, off.height).data;
      let transparent = false;
      for (let i = 3; i < px.length; i += 16) if (px[i] < 250){ transparent = true; break; }
      const m = new Float32Array(HW * HH);
      for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++){
        let v = 0;
        for (let j = 0; j < 4; j++) for (let i = 0; i < 4; i++){
          const q = ((y * 4 + j) * off.width + x * 4 + i) * 4, a = px[q + 3] / 255;
          const lum = (px[q] * 0.299 + px[q + 1] * 0.587 + px[q + 2] * 0.114) / 255;
          v += transparent ? a : 1 - lum;   // a logo on transparent: its alpha; on white: its ink
        }
        m[y * HW + x] = v / 16;
      }
      // light logo on transparent (white mark): alpha already covers it
      H = new Float32Array(m);
      const tmp = new Float32Array(H.length), r = 2;
      for (let pass = 0; pass < 3; pass++){
        for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++){
          let a = 0, n = 0;
          for (let i = -r; i <= r; i++){ const xx = x + i; if (xx >= 0 && xx < HW){ a += H[y * HW + xx]; n++; } }
          tmp[y * HW + x] = a / n;
        }
        for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++){
          let a = 0, n = 0;
          for (let j = -r; j <= r; j++){ const yy = y + j; if (yy >= 0 && yy < HH){ a += tmp[yy * HW + x]; n++; } }
          H[y * HW + x] = a / n;
        }
      }
      // keep the silhouette crisp: the blurred dome only lives inside the mark
      for (let i = 0; i < H.length; i++) H[i] = m[i] > 0.35 ? 0.25 + H[i] * 0.75 : m[i] * H[i];
      // only the mark and a margin around it (for the halo, ripples and tears) is ever drawn
      bx0 = HW; bx1 = 0; by0 = HH; by1 = 0;
      for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++) if (H[y * HW + x] > 0.06){
        if (x < bx0) bx0 = x; if (x > bx1) bx1 = x; if (y < by0) by0 = y; if (y > by1) by1 = y;
      }
      bx0 = Math.max(0, bx0 - 12); bx1 = Math.min(HW - 1, bx1 + 12); by0 = Math.max(0, by0 - 4); by1 = Math.min(HH - 1, by1 + 4);
      del = new Float32Array(HW * HH);
      for (let y = 0; y < HH; y++) for (let x = 0; x < HW; x++){
        const dx = x / HW - 0.5, dy = y / HH - 0.5;
        del[y * HW + x] = Math.hypot(dx, dy * 0.6) * 1700 + hash(x, y) * 500;
      }
    }
    const hAt = (x, y) => {
      x = x | 0; y = y | 0;
      return (x < 0 || y < 0 || x >= HW || y >= HH) ? 0 : H[y * HW + x];
    };
    function draw(now){
      const t = now - start, sec = t * (still ? 0.0005 : 0.001);   // reduced motion: same flow, half speed, no tears or drips
      const r = canvas.getBoundingClientRect(), has = mouse.x > -9999;
      const mx = (mouse.x - r.left) / s.cw, my = (mouse.y - r.top) / s.ch;
      const inside = has && mx > -10 && my > -10 && mx < HW + 10 && my < HH + 10;
      // the ripple swells in under the pointer and settles back out when it leaves or the finger lifts
      if (inside){ rcx = mx; rcy = my; }
      rip += ((inside ? 1 : 0) - rip) * (inside ? 0.25 : 0.08);
      tiltX += ((inside ? clamp((mx - HW / 2) / HW, -0.6, 0.6) : Math.sin(sec * 0.4) * 0.25) - tiltX) * 0.06;
      tiltY += ((inside ? clamp((my - HH / 2) / HH, -0.6, 0.6) : Math.cos(sec * 0.3) * 0.2) - tiltY) * 0.06;
      if (!still && now > nextTear){
        tear = {y: Math.random() * HH | 0, h: 1 + (Math.random() * 4 | 0), dx: (Math.random() < 0.5 ? -1 : 1) * (2 + (Math.random() * 7 | 0)), until: now + 110};
        nextTear = now + 3000 + Math.random() * 4000;
      }
      const tr = tear && now < tear.until ? tear : null;
      s.clear();
      // the molten surface for this frame, computed once per cell instead of per lookup
      if (!S || S.length !== H.length) S = new Float32Array(H.length);
      for (let y = by0; y <= by1; y++) for (let x = bx0; x <= bx1; x++){
        const i = y * HW + x;
        S[i] = H[i] > 0.01 ? H[i] * (0.7 + 0.6 * vnoise(x * 0.06 + sec * 0.22, y * 0.1 - sec * 0.17)) : 0;
      }
      const surf = (a, b) => { a = a | 0; b = b | 0; return (a < 0 || b < 0 || a >= HW || b >= HH) ? 0 : S[b * HW + a]; };
      const R = Math.max(8, HW * 0.12);
      let best = 0, bx = 0, by = 0;
      // molten wobble: the surface flows sideways in slow waves (one offset per row, one per column)
      if (!rowW || rowW.length !== HH){ rowW = new Float32Array(HH); colW = new Float32Array(HW); }
      for (let y = by0; y <= by1; y++) rowW[y] = Math.sin(y * 0.18 + sec * 1.4) * 0.9 + Math.sin(y * 0.05 - sec * 0.7) * 1.2;
      for (let x = bx0; x <= bx1; x++) colW[x] = Math.sin(x * 0.11 + sec * 1.1) * 0.6;
      for (let y = by0; y <= by1; y++){
        const shift = tr && y >= tr.y && y < tr.y + tr.h ? tr.dx : 0;
        for (let x = bx0; x <= bx1; x++){
          let sx = x - shift + rowW[y];
          let sy = y + colW[x];
          let near = 0;
          if (rip > 0.01){
            const px = x - rcx, py = (y - rcy) * 1.55, d = Math.hypot(px, py);
            if (d < R){ near = (1 - d / R) * rip; const w = Math.sin(d * 0.9 - sec * 9) * near * 1.6; sx -= px / (d || 1) * w; sy -= py / (d || 1) * w * 0.6; }
          }
          const h = hAt(sx, sy);
          const age = t - del[y * HW + x];
          if (h < 0.06){
            // chromatic halo just outside the metal
            if (age > 0){
              if (hAt(sx + 1.5, sy) > 0.3) s.glyph(':', x, y, BLUE, 0.35);
              else if (hAt(sx - 1.5, sy) > 0.3) s.glyph(':', x, y, RED, 0.3);
            }
            continue;
          }
          if (age < 0){ if (Math.random() < 0.05) s.glyph(pick(NOISE), x, y, MID, 0.35); continue; }
          // the metal's surface: the inflated mark plus slow molten lumps rolling through it
          // surface normal, plus the tilt toward the cursor
          const nx = (surf(sx + 1, sy) - surf(sx - 1, sy)) * 3.2 + tiltX * 0.8;
          const ny = (surf(sx, sy + 1) - surf(sx, sy - 1)) * 3.2 + tiltY * 0.8;
          // reflect a studio sky: a hard horizon and bright softbox stripes drifting across
          const rr = ny * 1.7 + nx * 0.6 + surf(sx, sy) * 1.1;
          const bands = 0.5 + 0.5 * Math.sin(rr * 7.5 + sec * 0.8);
          const horizon = rr < 0.15 ? 0.3 : 1;
          const fres = clamp(Math.hypot(nx, ny) * 0.9, 0, 1);
          let v = 0.12 + Math.pow(bands, 3) * horizon + fres * 0.3;
          v = clamp(v * v * (3 - 2 * clamp(v, 0, 1)), 0, 1);   // push toward hard black and white, like polished metal
          // the silhouette always reads: a lit rim where the metal rolls over its edge, never fully black inside
          const rim = hAt(sx - 1.5, sy) < 0.06 || hAt(sx + 1.5, sy) < 0.06 || hAt(sx, sy - 1) < 0.06 || hAt(sx, sy + 1) < 0.06;
          v = rim ? Math.max(v, 0.7 + 0.3 * bands) : Math.max(v, 0.2);
          let scramble = !!shift || (near > 0.35 && Math.random() < near * 0.6);
          if (age < 350){ v *= age / 350; scramble = scramble || Math.random() > age / 350; }
          const c = scramble ? pick(NOISE) : RAMP[clamp(1 + (v * (RAMP.length - 2)) | 0, 1, RAMP.length - 1)];
          const ci = v > 0.86 ? WHITE : v > 0.62 ? ICE : v > 0.38 ? LIGHT : v > 0.18 ? MID : GREY;
          // dispersion: the brightest highlights leave a red and a blue ghost either side
          // the darkest metal reads as black chrome with half its glyphs, in a fixed checker (no flicker)
          if (sparse && v < 0.3 && !scramble && ((x + y) & 1)) continue;
          if (v > 0.9 && !rim){ s.glyph(c, x - 1, y, RED, 0.22); s.glyph(c, x + 1, y, BLUE, 0.22); }
          s.glyph(c, x, y, ci, 0.35 + v * 0.65);
          if (v > best && Math.random() < 0.3){ best = v; bx = x; by = y; }
          if (shift && Math.random() < 0.4) s.glyph(pick(NOISE), x - Math.sign(shift) * 2, y, shift > 0 ? RED : BLUE, 0.5);
        }
      }
      if (!still){
        // drips gather on the lowest edges and fall away
        if (t > 2000 && now > nextDrip){
          for (let k = 0; k < 40; k++){
            const x = Math.random() * HW | 0, y = Math.random() * HH | 0;
            if (H[y * HW + x] > 0.3 && hAt(x, y + 1) < 0.06){ drips.push({x, y: y + 1, v: 0, born: now}); break; }
          }
          nextDrip = now + 500 + Math.random() * 900;
        }
        drips = drips.filter(d => {
          const age = (now - d.born) / 1000;
          const y = d.y + (age < 0.6 ? age * 2 : 1.2 + (age - 0.6) * (age - 0.6) * 60);
          if (y > HH + 2) return false;
          const a = age < 0.6 ? 0.7 : 0.9;
          s.glyph(age < 0.6 ? '.' : '|', d.x, Math.round(y), ICE, a);
          if (age >= 0.6) s.glyph(':', d.x, Math.round(y) - 1, MID, a * 0.5);
          return true;
        });
        if (t > 1600 && now > nextGlint && glints.length < 2 && best > 0.8){
          glints.push({x: bx, y: by, born: now});
          nextGlint = now + 1100 + Math.random() * 2000;
        }
        glints = glints.filter(g => now - g.born < 1000);
        glints.forEach(g => s.glint(g.x, g.y, (now - g.born) / 1000));
        if (tr) s.slice(2, 20);
      }
      s.ctx.globalAlpha = 1;
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (!visible || !H || now - last < 41) return;   // 24fps: liquid metal moves slowly, and every frame is ~3,000 glyph draws
      last = now; draw(now);
    }
    img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => fontsReady.then(() => {
      try { heightField(); } catch (e){ return; }
      canvas.classList.add('is-live');
      if ('IntersectionObserver' in window) new IntersectionObserver(es => {
        visible = es[0].isIntersecting;
        if (visible && !start){ start = performance.now(); nextGlint = start + 1600; nextTear = start + 3000; nextDrip = start + 2200; }
      }, {rootMargin: '0px 0px -10% 0px'}).observe(canvas);
      else { visible = true; start = performance.now(); }
      requestAnimationFrame(frame);
    });
    img.src = src;
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (H) heightField(); }, 200); });
  }

  /* ── plate: the gate's full-screen backdrop ──
     Two layers on one fixed canvas. Behind: a field of dotted contour lines,
     the isolines of slowly morphing noise, like a terrain scan that never
     settles; the pointer swirls it. In front: short lines of code that pour
     off the specimen and drift away on the field's currents, the object
     turning into data. Typing feeds it energy; a rejected key scatters the
     code red and blue, an accepted one floods it. The area behind the key
     field ("quiet") is kept dim so the type always reads.
     Returns {pulse, level, mode}. */
  function plate(canvas, opts){
    opts = opts || {};
    const fine = matchMedia('(hover: hover) and (min-width: 990px)').matches;
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const ctx = canvas.getContext('2d');
    const CELL = fine ? 5 : 6, NODE = CELL * 2, LV = 8, MAXS = fine ? 110 : 46;
    const TOKENS = ['vz.prism', 'refract()', '0x3F2A', 'λ 380nm', 'λ 700nm', 'spectrum[7]', 'ior 1.52', 'trace()', 'return light',
      '::render', 'obs_001', 'null', '0110 1001', 'sealed', 'unseen', 'idx++', 'disperse(λ)', 'chapter_001', 'ETHAL', 'signal', '#000', '#fff',
      'record.open()', 'n = 1.52', 'θ 41.2°', 'rgb → λ', 'decay 0.94', 'buffer', 'origin unknown', 'partial render'];
    let W = 0, H = 0, C = 0, R = 0, NC = 0, NR = 0, nodes = null, dpr = 1;
    let energy = 0, lvl = 0, mode = 'idle', modeT = 0, ph = 0, last = 0, lastT = performance.now(), streams = [], acc = 0;
    function size(){
      dpr = Math.min(devicePixelRatio || 1, fine ? 1.5 : 1.25);
      // the canvas's own box: full screen on the gate, a section's area elsewhere
      const box = canvas.getBoundingClientRect();
      W = Math.max(1, Math.round(box.width)) || innerWidth; H = Math.max(1, Math.round(box.height)) || innerHeight;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      C = Math.ceil(W / CELL); R = Math.ceil(H / CELL);
      NC = Math.ceil(W / NODE) + 2; NR = Math.ceil(H / NODE) + 2;
      nodes = new Float32Array(NC * NR);
    }
    size();
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(size, 150); });

    function spawn(rect){
      // from the specimen's silhouette, mostly its upper half, flowing outward and up
      const cx = rect.left + rect.width / 2, cy = rect.top + rect.height * 0.46;
      const rx = Math.min(rect.width * 0.22, rect.height * 0.3), ry = rect.height * 0.33;
      const a = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 1.5;
      const k = 0.55 + Math.random() * 0.45;
      const x = cx + Math.cos(a) * rx * k, y = cy + Math.sin(a) * ry * k;
      const sp = 14 + Math.random() * 26 + energy * 60;
      streams.push({x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 10, life: 0,
        ttl: 2.6 + Math.random() * 2.4, t: pick(TOKENS), tint: Math.random() < 0.1 ? (Math.random() < 0.5 ? 1 : 2) : 0});
    }

    function draw(now){
      const dt = Math.min(0.1, (now - lastT) / 1000); lastT = now;
      energy *= Math.pow(0.35, dt);
      const surge = mode === 'grant' ? 1 : 0, shock = mode === 'deny' ? Math.max(0, 1 - (now - modeT) / 1400) : 0;
      if (mode === 'deny' && shock <= 0) mode = 'idle';
      ph += dt * (still ? 0.02 : 0.05 + energy * 0.35 + lvl * 0.02 + surge * 0.6);
      ctx.clearRect(0, 0, W, H);

      // contour field: noise sampled on a coarse lattice, interpolated per cell
      // (everything is measured in the canvas's own coordinates)
      const ob = canvas.getBoundingClientRect();
      const s = 0.055, mx = mouse.x - ob.left, my = mouse.y - ob.top;
      const rel = r => r && {left: r.left - ob.left, right: r.right - ob.left, top: r.top - ob.top, bottom: r.bottom - ob.top, width: r.width, height: r.height};
      for (let j = 0; j < NR; j++) for (let i = 0; i < NC; i++){
        let x = i * s, y = j * s;
        const dx = i * NODE - mx, dy = j * NODE - my, d = Math.hypot(dx, dy);
        if (d < 220){ const k = (1 - d / 220) * (1 - d / 220) * 0.9; x += -dy / 220 * k; y += dx / 220 * k; }
        nodes[j * NC + i] = vnoise(x + ph, y - ph * 0.6) * 0.62 + vnoise(x * 0.5 - ph * 0.7 + 7.3, y * 0.5 + ph * 0.45 - 2.1) * 0.38
          + shock * 0.25 * Math.sin(d * 0.03 - (now - modeT) * 0.012);
      }
      const q = rel(opts.quiet && opts.quiet.getBoundingClientRect());
      const glow = rel(opts.source && opts.source.getBoundingClientRect());
      // dots are sorted into a few brightness buckets and each bucket is filled as one path
      const B = [new Path2D(), new Path2D(), new Path2D(), new Path2D(), new Path2D()];
      for (let y = 0; y < R; y++){
        const fy = y * CELL / NODE, j = fy | 0, ty = fy - j;
        for (let x = 0; x < C; x++){
          const fx = x * CELL / NODE, i = fx | 0, tx = fx - i, b = j * NC + i;
          const n0 = nodes[b], n1 = nodes[b + 1], n2 = nodes[b + NC], n3 = nodes[b + NC + 1];
          const v = n0 + (n1 - n0) * tx + (n2 - n0) * ty + (n0 - n1 - n2 + n3) * tx * ty;
          const f = v * LV, r = Math.round(f), d = Math.abs(f - r);
          if (d > 0.075) continue;
          const px = x * CELL, py = y * CELL, major = r % 3 === 0;
          let a = (1 - d / 0.075) * (major ? 0.42 : 0.2);
          // the page's edges and the key field stay calm; the area around the specimen glows a little
          if (q && px > q.left - 30 && px < q.right + 30 && py > q.top - 20 && py < q.bottom + 20) a *= 0.18;
          if (glow){ const gx = px - (glow.left + glow.width / 2), gy = py - (glow.top + glow.height / 2); a *= 0.7 + 0.6 * Math.max(0, 1 - Math.hypot(gx, gy * 1.3) / (glow.height * 0.9)); }
          const sz = major ? 2 : 1.5;
          B[Math.min(4, a * 9 | 0)].rect(px, py, sz, sz);
        }
      }
      ctx.fillStyle = '#fff';
      for (let k = 0; k < 5; k++){ ctx.globalAlpha = (k + 0.6) / 9; ctx.fill(B[k]); }

      // code streams
      if (glow && glow.bottom > 0 && glow.top < H){
        acc += dt * ((still ? 4 : 16) + energy * 90 + lvl * 6 + surge * 160 + shock * 60);
        while (acc >= 1 && streams.length < MAXS * (surge ? 2 : 1)){ acc -= 1; spawn(glow); }
        if (acc > 3) acc = 0;
      }
      ctx.font = (fine ? 10 : 9) + 'px ' + MONO; ctx.textBaseline = 'middle';
      streams = streams.filter(p => {
        p.life += dt;
        if (p.life > p.ttl) return false;
        // ride the field's currents (its gradient, turned 90°) plus a gentle lift
        const gx = vnoise(p.x * 0.004 + ph, p.y * 0.004) - 0.5, gy = vnoise(p.x * 0.004 + 9.1, p.y * 0.004 - ph) - 0.5;
        p.vx += (gy * 60 + shock * (Math.random() - 0.5) * 300) * dt; p.vy += (-gx * 60 - 6) * dt;
        p.x += p.vx * dt * (1 + surge * 2); p.y += p.vy * dt * (1 + surge * 2);
        const fade = Math.min(1, p.life / 0.4) * (1 - p.life / p.ttl);
        const tint = shock > 0.2 ? (p.x % 2 < 1 ? 1 : 2) : p.tint;
        ctx.globalAlpha = fade * (fine ? 0.8 : 0.7);
        ctx.fillStyle = tint === 1 ? 'rgb(255,90,115)' : tint === 2 ? 'rgb(95,165,255)' : 'rgb(236,240,245)';
        ctx.fillText(p.t, p.x, p.y);
        // a short trail of light behind it, the way the tree's branches stream
        ctx.globalAlpha = fade * 0.35;
        const sp = Math.hypot(p.vx, p.vy) || 1;
        for (let k = 1; k <= 4; k++) ctx.fillRect(p.x - p.vx / sp * k * 5, p.y - p.vy / sp * k * 5, 1.5, 1.5);
        return true;
      });
      ctx.globalAlpha = 1;
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (now - last < (fine ? 33 : 41)) return;   // 30fps desktop, 24 on phones
      last = now; draw(now);
    }
    fontsReady.then(() => requestAnimationFrame(frame));
    return {
      pulse(v){ energy = Math.min(1.5, energy + (v || 0.4)); },
      level(l){ lvl = l; },
      mode(m){ mode = m; modeT = performance.now(); if (m === 'deny') energy = 1; }
    };
  }

  /* ── dither: an ordered-dither field behind a section ──
     Broad cloud masses and slow waves, quantised through a 4×4 Bayer matrix
     into a few greys so the square cells read. Computed once per cell into a
     small image and scaled up with no smoothing, ~8 times a second. The bright
     mass leans toward the pointer. Kept dim: it is atmosphere, not content. */
  function dither(canvas){
    const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => (v + 0.5) / 16);
    const PAL = [[3, 3, 3], [11, 11, 12], [20, 20, 22], [34, 35, 37], [58, 59, 62], [104, 105, 108]];
    const still = matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fine = matchMedia('(hover: hover) and (min-width: 990px)').matches;
    const CELL = fine ? 6 : 7;
    const ctx = canvas.getContext('2d', {alpha: false});
    const off = document.createElement('canvas'), octx = off.getContext('2d');
    let cols = 1, rows = 1, img = null, visible = true, last = 0, t0 = performance.now(), lx = 0, ly = 0;
    const sm = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
    const fbm = (x, y) => vnoise(x, y) * 0.55 + vnoise(x * 2.03, y * 2.03) * 0.3 + vnoise(x * 4.1, y * 4.1) * 0.15;
    function size(){
      const r = canvas.getBoundingClientRect();
      if (!r.width) return;
      cols = Math.ceil(r.width / CELL); rows = Math.ceil(r.height / CELL);
      canvas.width = cols * CELL; canvas.height = rows * CELL;
      off.width = cols; off.height = rows;
      img = octx.createImageData(cols, rows);
    }
    function draw(now){
      if (!img) return;
      const t = still ? 0 : (now - t0) * 0.001 * 0.06;
      const r = canvas.getBoundingClientRect();
      const tx = mouse.x > -9999 ? clamp((mouse.x - r.left) / r.width * 2 - 1, -1, 1) * 0.35 : -0.22;
      const ty = mouse.x > -9999 ? clamp((mouse.y - r.top) / r.height * 2 - 1, -1, 1) * 0.3 : 0.08;
      lx += (tx - lx) * 0.08; ly += (ty - ly) * 0.08;
      const d = img.data;
      for (let y = 0; y < rows; y++){
        const ny = (y / rows - 0.5) * 2;
        for (let x = 0; x < cols; x++){
          const nx = (x / cols - 0.5) * 2;
          const vig = 1 - sm(0.2, 1.15, Math.sqrt(nx * nx * 0.84 + ny * ny * 1.28));
          const wave = Math.sin(nx * 2.8 + ny * 1.2 + t) * 0.18 + Math.sin(nx * -1.4 + ny * 3.8 - t * 0.8) * 0.14;
          const ridge = sm(0.5, 0.95, fbm(nx * 1.35 + t * 0.16, ny * 1.35 - t * 0.08) + wave);
          const mass = sm(1.0, 0.15, Math.hypot(nx - lx, ny - ly));
          const b = clamp(ridge * vig * 0.85 + mass * 0.22, 0, 1);
          const c = PAL[Math.min(PAL.length - 1, Math.floor(clamp(b + BAYER[(y & 3) * 4 + (x & 3)] * 0.2, 0, 0.999) * PAL.length))];
          const i = (y * cols + x) * 4;
          d[i] = c[0]; d[i + 1] = c[1]; d[i + 2] = c[2]; d[i + 3] = 255;
        }
      }
      octx.putImageData(img, 0, 0);
      ctx.imageSmoothingEnabled = false;
      ctx.drawImage(off, 0, 0, cols * CELL, rows * CELL);
    }
    function frame(now){
      requestAnimationFrame(frame);
      if (!visible || now - last < 120) return;
      last = now; draw(now);
    }
    size();
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { size(); draw(performance.now()); }, 150); });
    if ('IntersectionObserver' in window) new IntersectionObserver(es => { visible = es[0].isIntersecting; }).observe(canvas);
    draw(performance.now());
    if (!still) requestAnimationFrame(frame);
  }

  /* ── scanReveal: a product photo arrives through a survey scan ──
     Two acts on one overlay.
     The sweep: a straight front (with a slight wave) rises from the bottom.
     A wire cage built from the photo itself rides it: horizontal survey lines
     lifted by the garment's light, sparser vertical ribs, brightest at the
     front with a faint red / blue split. Behind the front the photo is set in
     ASCII made from its own light (garment edges traced bright), scrambling
     where it has just been reached and settling below. Above it, black.
     The dissolve: once the whole photo is characters, the cells come apart in
     a scattered order, each scrambling as it fades, uncovering the photo; the
     wire trace fades with them. When the last character is gone the overlay
     is already empty, and only then is it removed, so nothing ever cuts.
     Geometry and per-cell values are computed once. ~1.7s the first time a
     photo is seen in a visit, about half that after. Returns {reveal, scan}. */
  function scanReveal(img, opts){
    opts = opts || {};
    const box = img.parentElement;
    if (!box || img.dataset.vzMat) return null;
    img.dataset.vzMat = '1';
    if (getComputedStyle(box).position === 'static') box.style.position = 'relative';
    const cv = document.createElement('canvas');
    cv.className = 'vz-mat vz-scan'; cv.setAttribute('aria-hidden', 'true');
    box.appendChild(cv);
    const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5].map(v => (v + 0.5) / 16 - 0.5);
    const sm = (a, b, v) => { const t = clamp((v - a) / (b - a), 0, 1); return t * t * (3 - 2 * t); };
    const key = (img.currentSrc || img.src || '').split('?')[0];
    let seen = false;
    try { seen = (JSON.parse(sessionStorage.getItem('vz_scanned') || '[]')).indexOf(key) > -1; } catch (e){}
    const k = (opts.speed || 1) * (seen ? 0.5 : 1);
    const SWEEP = 820 * k, FADE = 300 * k, SPREAD = 620 * k;
    let s = null, W = 0, H = 0, L = null, state = 'cover', pending = false, t0 = 0;
    let far = 1, rimW = 1, trailW = 1, fresh = 1;
    let dC = null, lumC = null, del = null, seg = null, segD = null;

    function place(){
      W = img.offsetWidth; H = img.offsetHeight;
      if (!W || !H) return false;
      Object.assign(cv.style, {left: img.offsetLeft + 'px', top: img.offsetTop + 'px', width: W + 'px', height: H + 'px'});
      const cw = clamp(W / 72, 4, 8);
      s = new Surface(cv, cw, cw * 1.6, 1.5);
      rimW = H * 0.045; trailW = H * 0.5; fresh = s.ch * 5;
      return true;
    }
    function lumAt(x, y){
      if (!L) return 0.3 + 0.35 * vnoise(x * 0.02, y * 0.02);
      const iw = img.naturalWidth || 1, ih = img.naturalHeight || 1, kk = Math.max(W / iw, H / ih);
      const u = (x - (W - iw * kk) / 2) / kk / iw, v = (y - (H - ih * kk) / 2) / kk / ih;
      if (u < 0 || v < 0 || u >= 1 || v >= 1) return 0;
      return L.lum[(v * L.H | 0) * L.W + (u * L.W | 0)];
    }
    // height above the bottom edge, with a gentle wave so the front isn't a ruler-straight wipe
    const wave = x => Math.sin(x * 0.019) * H * 0.025 + Math.sin(x * 0.047 + 1.3) * H * 0.01;
    function build(){
      const C = s.cols, R = s.rows, n = C * R;
      dC = new Float32Array(n); lumC = new Float32Array(n); del = new Float32Array(n);
      far = 0;
      for (let y = 0; y < R; y++) for (let x = 0; x < C; x++){
        const px = (x + 0.5) * s.cw, py = (y + 0.5) * s.ch, i = y * C + x;
        dC[i] = (H - py) + wave(px) + BAYER[(y & 3) * 4 + (x & 3)] * s.ch * 2;
        lumC[i] = lumAt(px, py);
        // the dissolve: scattered, a little earlier toward the bottom where the sweep began
        del[i] = hash(x * 1.3, y * 2.1) * SPREAD + (1 - y / R) * SPREAD * 0.35;
        if (dC[i] > far) far = dC[i];
      }
      // characters read even on a black garment: lifted floor, plus its edges traced bright
      const base = Float32Array.from(lumC), at = (x, y) => base[clamp(y, 0, R - 1) * C + clamp(x, 0, C - 1)];
      for (let y = 0; y < R; y++) for (let x = 0; x < C; x++){
        const edge = Math.abs(at(x + 1, y) - at(x - 1, y)) + Math.abs(at(x, y + 1) - at(x, y - 1));
        lumC[y * C + x] = clamp(0.3 + base[y * C + x] * 0.6 + edge * 1.5, 0, 1);
      }
      const SY = Math.max(6, H / 95), AMP = Math.min(16, H * 0.028), SX = 4, segs = [];
      for (let y = SY / 2; y < H; y += SY){
        let px = 0, py = y - (lumAt(0, y) - 0.45) * AMP * 2;
        for (let x = SX; x <= W; x += SX){ const ny = y - (lumAt(x, y) - 0.45) * AMP * 2; segs.push(px, py, x, ny); px = x; py = ny; }
      }
      const RX = Math.max(18, W / 20);
      for (let x = RX / 2; x < W; x += RX){
        let px = x - (lumAt(x, 0) - 0.45) * AMP * 0.6, py = 0;
        for (let y = SY; y <= H; y += SY){ const nx = x - (lumAt(x, y) - 0.45) * AMP * 0.6; segs.push(px, py, nx, y); px = nx; py = y; }
      }
      seg = new Float32Array(segs); segD = new Float32Array(segs.length / 4);
      for (let i = 0; i < segD.length; i++){
        const mx = (seg[i * 4] + seg[i * 4 + 2]) / 2, my = (seg[i * 4 + 1] + seg[i * 4 + 3]) / 2;
        segD[i] = (H - my) + wave(mx);
      }
    }
    function cover(){ if (!s) return; s.clear(); s.ctx.globalAlpha = 1; s.ctx.fillStyle = '#050505'; s.ctx.fillRect(0, 0, s.w, s.h); }
    function finish(){
      state = 'done';
      try { const ks = JSON.parse(sessionStorage.getItem('vz_scanned') || '[]'); if (ks.indexOf(key) < 0){ ks.push(key); sessionStorage.setItem('vz_scanned', JSON.stringify(ks.slice(-80))); } } catch (e){}
      cv.remove();   // already empty: every cell has faded out
    }
    function frame(now){
      if (state !== 'run') return;
      const t = now - t0, C = s.cols, cw = s.cw, ch = s.ch, ctx = s.ctx;
      const e = clamp(t / SWEEP, 0, 1);
      const r = (1 - Math.pow(1 - e, 1.6)) * (far + rimW);            // the front's height above the bottom
      const td = t - SWEEP;                                          // time into the dissolve (negative during the sweep)
      s.clear();
      // black under every cell that is still covered (ahead of the front, or not yet dissolved)
      const shade = new Path2D();
      let alive = 0;
      for (let i = 0; i < dC.length; i++){
        const age = td - del[i];
        if (age >= FADE) continue;
        alive++;
        if (age <= 0) shade.rect((i % C) * cw, (i / C | 0) * ch, cw + 0.5, ch + 0.5);
      }
      ctx.globalAlpha = 1; ctx.fillStyle = '#050505'; ctx.fill(shade);
      // the characters: behind the front during the sweep; then each one codes itself apart
      for (let i = 0; i < dC.length; i++){
        const d = dC[i];
        if (d > r) continue;                                         // not reached yet: stays black
        const age = td - del[i], x = i % C, y = i / C | 0;
        if (age >= FADE) continue;
        if (age > 0){
          const a = 1 - age / FADE;
          ctx.globalAlpha = a; ctx.fillStyle = '#050505'; ctx.fillRect(x * cw, y * ch, cw + 0.5, ch + 0.5);
          s.lit(x, y, lumC[i] * a, now, Math.random() < 0.55);
        } else {
          s.lit(x, y, lumC[i], now, (r - d) < fresh ? Math.random() < 0.6 : Math.random() < 0.05);
        }
      }
      ctx.globalAlpha = 1;
      // the cage: brightest on the front during the sweep, a faint trace behind it, fading out with the dissolve
      const wire = (e < 1 ? Math.min(1, e / 0.05) : 1) * (1 - sm(0, SPREAD * 0.8, td));
      if (wire > 0.01){
        const B = [new Path2D(), new Path2D(), new Path2D(), new Path2D(), new Path2D()];
        const rr = e < 1 ? r : far + rimW * 4;                        // after the sweep the rim has passed off the top
        for (let i = 0; i < segD.length; i++){
          const d = segD[i];
          if (d > rr + rimW * 2.5) continue;
          const rim = e < 1 ? Math.exp(-((d - rr) / rimW) * ((d - rr) / rimW)) : 0, trail = sm(rr, rr - trailW, d);
          const a = Math.min(1, (rim * 1.6 + trail * 0.2) * wire);
          if (a < 0.04) continue;
          const b = Math.min(4, a * 5 | 0), o = i * 4;
          B[b].moveTo(seg[o], seg[o + 1]); B[b].lineTo(seg[o + 2], seg[o + 3]);
        }
        ctx.lineWidth = 1;
        for (let b = 0; b < 5; b++){
          const a = (b + 1) / 5;
          if (b >= 3){   // the leading edge splits faintly, like light through the prism
            ctx.globalAlpha = a * 0.45;
            ctx.strokeStyle = 'rgb(255,70,100)'; ctx.translate(-1, 0); ctx.stroke(B[b]);
            ctx.strokeStyle = 'rgb(70,160,255)'; ctx.translate(2, 0); ctx.stroke(B[b]);
            ctx.translate(-1, 0);
          }
          ctx.globalAlpha = a; ctx.strokeStyle = 'rgb(242,244,248)'; ctx.stroke(B[b]);
        }
        ctx.globalAlpha = 1;
      }
      if (td > 0 && !alive && wire <= 0.01){ finish(); return; }
      requestAnimationFrame(frame);
    }
    function start(){
      if (state !== 'cover') return;
      if (!place()) return;
      build();
      state = 'run'; t0 = performance.now();
      requestAnimationFrame(frame);
    }

    if (place()) cover();
    const src = new Image();
    src.crossOrigin = 'anonymous';
    src.onload = () => { L = lumaOf(src, 220); if (pending){ pending = false; start(); } };
    src.onerror = () => { if (pending){ pending = false; start(); } };
    src.src = img.currentSrc || img.src;
    let rt;
    addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(() => { if (state === 'cover'){ if (place()) cover(); } else if (state === 'run'){ place(); build(); } }, 150); });
    return {
      reveal(){
        if (state !== 'cover') return;
        // wait briefly for the photo's light so the cage and characters are built from it
        if (!L && !src.complete){ pending = true; setTimeout(() => { if (pending){ pending = false; start(); } }, 600); return; }
        start();
      },
      scan(){}
    };
  }

  return {
    field, logo, rack, specimen, cursor, beam, materialize, lens, wordmark, relief, story, chrome, plate, dither, scanReveal,
    // a keystroke at the gate: photons down the light path, data rushes the wires
    lightPulse(){
      if (beamApi) beamApi.pulse();
      if (wireSvg){ wireSvg.classList.add('hot'); clearTimeout(wireSvg.t); wireSvg.t = setTimeout(() => wireSvg.classList.remove('hot'), 400); }
    },
    lightLevel(l){ if (beamApi) beamApi.level(l); }
  };
})();
